import { useCallback, useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { useTemplateStore, useAuthStore } from "@/store";
import { toast } from "sonner";
import { getTextPositionPreset, TEXT_POSITION_PRESETS } from "@/lib/textPositionPresets";
import {
  Play,
  Pause,
  ArrowLeft,
  Save,
  Plus,
  Image,
  Type,
  Video,
  Trash2,
  Settings,
  Layers,
  Lock,
  Unlock,
  Sparkles,
  Loader2,
  Eye,
  EyeOff,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Upload,
  Bold,
  Italic,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Zap,
  Music,
  Copy,
  ClipboardPaste,
  Square,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import axios from "axios";
import ShapeLayer from "@/components/ShapeLayer";
import ShapeSettings from "@/components/ShapeSettings";
import TextWipeLayer from "@/components/TextWipeLayer";
import { getAnimationAudioGain } from "@/lib/audioTransition";


const getLocalMediaDuration = (file) => new Promise((resolve) => {
  if (!file || (!file.type.startsWith("video/") && !file.type.startsWith("audio/"))) {
    resolve(null);
    return;
  }

  const element = document.createElement(file.type.startsWith("audio/") ? "audio" : "video");
  const objectUrl = URL.createObjectURL(file);
  const cleanup = () => URL.revokeObjectURL(objectUrl);

  element.preload = "metadata";
  element.onloadedmetadata = () => {
    const duration = Number.isFinite(element.duration) && element.duration > 0 ? element.duration : null;
    cleanup();
    resolve(duration);
  };
  element.onerror = () => {
    cleanup();
    resolve(null);
  };
  element.src = objectUrl;
});

const getRemoteVideoDuration = (mediaUrl) => new Promise((resolve) => {
  if (!mediaUrl) {
    resolve(null);
    return;
  }

  const video = document.createElement("video");
  video.preload = "metadata";
  video.onloadedmetadata = () => {
    const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
    video.removeAttribute("src");
    video.load();
    resolve(duration);
  };
  video.onerror = () => {
    video.removeAttribute("src");
    video.load();
    resolve(null);
  };
  video.src = mediaUrl;
});


const getMediaCornerRadiusStyle = (layer, scale = 1) => {
  const all = layer.borderRadius ?? 0;
  const topLeft = layer.borderTopLeftRadius ?? all;
  const topRight = layer.borderTopRightRadius ?? all;
  const bottomRight = layer.borderBottomRightRadius ?? all;
  const bottomLeft = layer.borderBottomLeftRadius ?? all;

  return {
    overflow: "hidden",
    borderTopLeftRadius: `${Number(topLeft || 0) * scale}px`,
    borderTopRightRadius: `${Number(topRight || 0) * scale}px`,
    borderBottomRightRadius: `${Number(bottomRight || 0) * scale}px`,
    borderBottomLeftRadius: `${Number(bottomLeft || 0) * scale}px`,
  };
};

// Animation preview helper functions
const cubicBezier = (t, p1, p2) => {
  const oneMinusT = 1 - t;
  return (3 * oneMinusT * oneMinusT * t * p1) + (3 * oneMinusT * t * t * p2) + (t * t * t);
};

const cubicBezierDerivative = (t, p1, p2) => {
  const oneMinusT = 1 - t;
  return (3 * oneMinusT * oneMinusT * p1) + (6 * oneMinusT * t * (p2 - p1)) + (3 * t * t * (1 - p2));
};

const solveBezierProgress = (progress, x1, y1, x2, y2) => {
  let t = progress;

  for (let i = 0; i < 5; i += 1) {
    const currentX = cubicBezier(t, x1, x2) - progress;
    const currentSlope = cubicBezierDerivative(t, x1, x2);

    if (Math.abs(currentSlope) < 1e-6) break;
    t -= currentX / currentSlope;
    t = Math.min(Math.max(t, 0), 1);
  }

  let lower = 0;
  let upper = 1;
  for (let i = 0; i < 8; i += 1) {
    const currentX = cubicBezier(t, x1, x2);
    if (Math.abs(currentX - progress) < 1e-5) break;
    if (currentX > progress) {
      upper = t;
    } else {
      lower = t;
    }
    t = (lower + upper) / 2;
  }

  return cubicBezier(t, y1, y2);
};

const applyAnimationEasing = (progress, easing = "linear") => {
  const clampedProgress = Math.min(Math.max(progress, 0), 1);

  switch (easing) {
    case "ease_in":
      return solveBezierProgress(clampedProgress, 0.55, 0, 1, 1);
    case "ease_out":
      return solveBezierProgress(clampedProgress, 0, 0, 0.45, 1);
    default:
      return clampedProgress;
  }
};

const getEasedExitProgress = (progress, easing = "linear") => {
  const exitPhase = 1 - Math.min(Math.max(progress, 0), 1);
  return 1 - applyAnimationEasing(exitPhase, easing);
};

const getLayerAnimationStyle = (layer, currentTime, isPlaying, canvasWidth, canvasHeight) => {
  if (!isPlaying) return {};
  
  const startTime = layer.start_time || 0;
  const duration = layer.duration || 0;
  const endTime = startTime + duration;
  const legacyAnimDuration = layer.animation_duration || 0.5;
  const entranceAnimDuration = layer.entrance_animation_duration ?? legacyAnimDuration;
  const exitAnimDuration = layer.exit_animation_duration ?? legacyAnimDuration;
  const loopDuration = layer.loop_duration || 1.0;
  const continuousStartPercent = layer.continuous_start_percent ?? 0;
  const continuousEndPercent = layer.continuous_end_percent ?? 100;
  const kenBurnsEndScale = layer.ken_burns_end_scale ?? 1.05;
  
  const entranceAnim = layer.entrance_animation || 'none';
  const exitAnim = layer.exit_animation || 'none';
  const entranceEasing = layer.entrance_easing || 'linear';
  const exitEasing = layer.exit_easing || 'linear';
  const continuousAnim = layer.continuous_animation || 'none';
  
  // Check if layer is currently visible
  if (currentTime < startTime || currentTime > endTime) {
    return { opacity: 0, pointerEvents: 'none' };
  }
  
  const timeIntoLayer = currentTime - startTime;
  const timeUntilEnd = endTime - currentTime;
  const layerProgress = duration > 0 ? timeIntoLayer / duration : 0;
  
  let style = {};
  
  // Apply entrance animation
  if (entranceAnim !== 'none' && timeIntoLayer < entranceAnimDuration) {
    const progress = timeIntoLayer / entranceAnimDuration;
    style = {
      ...style,
      ...getEntranceAnimation(entranceAnim, applyAnimationEasing(progress, entranceEasing)),
    };
  }
  // Apply exit animation
  else if (exitAnim !== 'none' && timeUntilEnd < exitAnimDuration) {
    const progress = timeUntilEnd / exitAnimDuration;
    style = {
      ...style,
      ...getExitAnimation(exitAnim, getEasedExitProgress(progress, exitEasing)),
    };
  }
  
  // Apply continuous animation (if no entrance/exit currently active)
  if (continuousAnim !== 'none' && 
      (entranceAnim === 'none' || timeIntoLayer >= entranceAnimDuration) &&
      (exitAnim === 'none' || timeUntilEnd >= exitAnimDuration)) {
    const normalizedStart = Math.min(Math.max(continuousStartPercent / 100, 0), 1);
    const normalizedEnd = Math.min(Math.max(continuousEndPercent / 100, 0), 1);
    if (normalizedEnd > normalizedStart && layerProgress >= normalizedStart && layerProgress <= normalizedEnd) {
      const activeDuration = duration * (normalizedEnd - normalizedStart);
      const activeTime = timeIntoLayer - duration * normalizedStart;
      const segmentProgress = activeDuration > 0 ? activeTime / activeDuration : 0;
      const loopProgress = loopDuration > 0 ? ((Math.max(activeTime, 0) % loopDuration) / loopDuration) : 0;
      const progress = continuousAnim === 'ken_burns' ? segmentProgress : loopProgress;
      style = {
        ...style,
        ...getContinuousAnimation(
          continuousAnim,
          progress,
          kenBurnsEndScale
        ),
      };
    }
  }
  
  return style;
};

const SUBTLE_SLIDE_DISTANCE_PX = 24;

const getSlideFadeAnimation = (direction, progress) => {
  const distance = (1 - progress) * SUBTLE_SLIDE_DISTANCE_PX;

  switch (direction) {
    case 'left':
      return { opacity: progress, transform: `translateX(${-distance}px)` };
    case 'right':
      return { opacity: progress, transform: `translateX(${distance}px)` };
    case 'top':
      return { opacity: progress, transform: `translateY(${-distance}px)` };
    case 'bottom':
    default:
      return { opacity: progress, transform: `translateY(${distance}px)` };
  }
};

const getCircularWipeAnimation = (progress) => {
  const radius = progress * 150;
  return {
    clipPath: `circle(${radius}% at 50% 50%)`,
    WebkitClipPath: `circle(${radius}% at 50% 50%)`,
  };
};

const getEntranceAnimation = (type, progress) => {
  switch (type) {
    case 'fade':
      return { opacity: progress };
    case 'flash':
      return {
        filter: `contrast(${1 - progress}) brightness(${1 + progress}) saturate(${1 - progress})`,
      };
    case 'slide_fade_in':
    case 'slide_fade_from_bottom':
      return getSlideFadeAnimation('bottom', progress);
    case 'slide_fade_from_left':
      return getSlideFadeAnimation('left', progress);
    case 'slide_fade_from_right':
      return getSlideFadeAnimation('right', progress);
    case 'slide_fade_from_top':
      return getSlideFadeAnimation('top', progress);
    case 'circular_wipe':
      return getCircularWipeAnimation(progress);
    case 'slide_left':
      return { transform: `translateX(${(1 - progress) * 100}%)` };
    case 'slide_right':
      return { transform: `translateX(${-(1 - progress) * 100}%)` };
    case 'slide_up':
      return { transform: `translateY(${(1 - progress) * 100}%)` };
    case 'slide_down':
      return { transform: `translateY(${-(1 - progress) * 100}%)` };
    case 'text_wipe':
      return { '--text-wipe-progress': progress, '--text-wipe-exit': 0 };
    case 'wipe_left':
      return { clipPath: `inset(0 ${(1 - progress) * 100}% 0 0)`, WebkitClipPath: `inset(0 ${(1 - progress) * 100}% 0 0)` };
    case 'wipe_right':
      return { clipPath: `inset(0 0 0 ${(1 - progress) * 100}%)`, WebkitClipPath: `inset(0 0 0 ${(1 - progress) * 100}%)` };
    case 'wipe_up':
      return { clipPath: `inset(0 0 ${(1 - progress) * 100}% 0)`, WebkitClipPath: `inset(0 0 ${(1 - progress) * 100}% 0)` };
    case 'wipe_down':
      return { clipPath: `inset(${(1 - progress) * 100}% 0 0 0)`, WebkitClipPath: `inset(${(1 - progress) * 100}% 0 0 0)` };
    case 'zoom_in':
      return { transform: `scale(${0.5 + progress * 0.5})`, opacity: progress };
    default:
      return {};
  }
};

const getExitAnimation = (type, progress) => {
  switch (type) {
    case 'fade':
      return { opacity: progress };
    case 'flash':
      return {
        filter: `contrast(${1 - progress}) brightness(${1 + progress}) saturate(${1 - progress})`,
      };
    case 'slide_fade_out':
    case 'slide_fade_to_top':
      return getSlideFadeAnimation('top', progress);
    case 'slide_fade_to_left':
      return getSlideFadeAnimation('left', progress);
    case 'slide_fade_to_right':
      return getSlideFadeAnimation('right', progress);
    case 'slide_fade_to_bottom':
      return getSlideFadeAnimation('bottom', progress);
    case 'circular_wipe':
      return getCircularWipeAnimation(progress);
    case 'slide_left':
      return { transform: `translateX(${-(1 - progress) * 100}%)` };
    case 'slide_right':
      return { transform: `translateX(${(1 - progress) * 100}%)` };
    case 'slide_up':
      return { transform: `translateY(${-(1 - progress) * 100}%)` };
    case 'slide_down':
      return { transform: `translateY(${(1 - progress) * 100}%)` };
    case 'text_wipe':
      return { '--text-wipe-progress': 1 - progress, '--text-wipe-exit': 1 };
    case 'wipe_left':
      return { clipPath: `inset(0 ${(1 - progress) * 100}% 0 0)`, WebkitClipPath: `inset(0 ${(1 - progress) * 100}% 0 0)` };
    case 'wipe_right':
      return { clipPath: `inset(0 0 0 ${(1 - progress) * 100}%)`, WebkitClipPath: `inset(0 0 0 ${(1 - progress) * 100}%)` };
    case 'wipe_up':
      return { clipPath: `inset(0 0 ${(1 - progress) * 100}% 0)`, WebkitClipPath: `inset(0 0 ${(1 - progress) * 100}% 0)` };
    case 'wipe_down':
      return { clipPath: `inset(${(1 - progress) * 100}% 0 0 0)`, WebkitClipPath: `inset(${(1 - progress) * 100}% 0 0 0)` };
    case 'zoom_out':
      return { opacity: progress * 0.5 };
    default:
      return {};
  }
};

const getKenBurnsMotion = (
  progress,
  kenBurnsEndScale = 1.05
) => {
  const clampedProgress = Math.min(Math.max(progress, 0), 1);
  const clampedEndScale = Math.min(Math.max(kenBurnsEndScale, 1), 2);
  const easedProgress = 0.5 - 0.5 * Math.cos(Math.PI * clampedProgress);
  const scale = 1 + (clampedEndScale - 1) * easedProgress;

  return {
    transform: `translate3d(0, 0, 0) scale(${scale})`,
    transformOrigin: 'center center',
    willChange: 'transform',
    backfaceVisibility: 'hidden',
  };
};

const getContinuousAnimation = (
  type,
  progress,
  kenBurnsEndScale = 1.05
) => {
  const twoPi = 2 * Math.PI;
  switch (type) {
    case 'bounce':
      const bounceOffset = Math.abs(Math.sin(progress * twoPi)) * 20;
      return { transform: `translateY(-${bounceOffset}px)` };
    case 'pulse':
      const pulseOpacity = 0.7 + Math.abs(Math.sin(progress * twoPi)) * 0.3;
      return { opacity: pulseOpacity };
    case 'rotate':
      const rotation = progress * 360;
      return { transform: `rotate(${rotation}deg)` };
    case 'ken_burns':
      return getKenBurnsMotion(progress, kenBurnsEndScale);
    default:
      return {};
  }
};

// Auto font-sizing utility function - calculates optimal font size to fit text in container
const getBackendTextMetrics = (containerWidth, containerHeight, style = {}) => {
  const textWidth = Math.max(1, Math.round(Number(containerWidth) || 0));
  const textHeight = Math.max(1, Math.round(Number(containerHeight) || 0));
  const defaultPad = Math.max(12, Math.floor(Math.min(textWidth, textHeight) * 0.06));
  const rawPaddingH = style.padding ?? style.paddingX ?? style.paddingHorizontal ?? defaultPad;
  const rawPaddingV = style.paddingY ?? style.paddingVertical ?? rawPaddingH;

  const parsePadding = (value, fallback) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : fallback;
  };

  const paddingH = Math.max(0, Math.min(parsePadding(rawPaddingH, defaultPad), Math.floor(textWidth / 3)));
  const paddingV = Math.max(0, Math.min(parsePadding(rawPaddingV, paddingH), Math.floor(textHeight / 3)));
  const innerW = Math.max(10, textWidth - paddingH * 2);
  const innerH = Math.max(10, textHeight - paddingV * 2);
  const descenderGuard = Math.max(6, Math.floor(innerH * 0.10));
  const safeInnerW = innerW;
  const safeInnerH = Math.max(10, innerH - descenderGuard);

  return {
    paddingH,
    paddingV,
    innerW,
    innerH,
    safeInnerW,
    safeInnerH,
    textX: paddingH,
    textY: paddingV + Math.round((innerH - safeInnerH) / 2),
  };
};

// Auto font-sizing utility function
const calculateAutoFontSize = (containerWidth, containerHeight, text, style = {}) => {
  if (!text || !containerWidth || !containerHeight) return 16;
  
  const metrics = getBackendTextMetrics(containerWidth, containerHeight, style);
  const fitWidth = metrics.safeInnerW;
  const fitHeight = metrics.safeInnerH;
  const temp = document.createElement('div');
  temp.style.position = 'absolute';
  temp.style.visibility = 'hidden';
  temp.style.width = `${fitWidth}px`;
  temp.style.whiteSpace = 'pre-wrap';
  temp.style.overflowWrap = 'normal';
  temp.style.wordBreak = 'normal';
  temp.style.lineHeight = style.lineHeight || '1.5';
  temp.style.fontFamily = style.fontFamily || 'Arial';
  temp.style.fontWeight = style.fontWeight || 'normal';
  temp.style.fontStyle = style.fontStyle || 'normal';
  temp.style.boxSizing = 'border-box';
  temp.textContent = text;
  document.body.appendChild(temp);

  const measureCanvas = calculateAutoFontSize._measureCanvas || document.createElement('canvas');
  calculateAutoFontSize._measureCanvas = measureCanvas;
  const measureContext = measureCanvas.getContext('2d');
  const words = String(text).split(/\s+/).filter(Boolean);

  const longestWordFits = (fontSize) => {
    if (!measureContext || words.length === 0) return true;
    measureContext.font = `${style.fontStyle || 'normal'} ${style.fontWeight || 'normal'} ${fontSize}px ${style.fontFamily || 'Arial'}`;
    return words.every((word) => measureContext.measureText(word).width <= fitWidth);
  };
  
  let minSize = 8;
  let maxSize = 200;
  let optimalSize = minSize;
  
  while (minSize <= maxSize) {
    const midSize = Math.floor((minSize + maxSize) / 2);
    temp.style.fontSize = `${midSize}px`;
    
    if (longestWordFits(midSize) && temp.offsetHeight <= fitHeight) {
      optimalSize = midSize;
      minSize = midSize + 1;
    } else {
      maxSize = midSize - 1;
    }
  }
  
  document.body.removeChild(temp);
  return optimalSize;
};

const hexToRgba = (hex, alpha = 1) => {
  const normalized = (hex || "#000000").replace("#", "");
  const safeHex = normalized.length === 3
    ? normalized.split("").map((char) => char + char).join("")
    : normalized.padEnd(6, "0").slice(0, 6);

  const r = parseInt(safeHex.slice(0, 2), 16) || 0;
  const g = parseInt(safeHex.slice(2, 4), 16) || 0;
  const b = parseInt(safeHex.slice(4, 6), 16) || 0;

  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha))})`;
};

const buildTextEffectShadow = ({
  strokeWidth = 0,
  strokeColor = "#000000",
  canvasZoom = 1,
}) => {
  const effects = [];
  const outsideStroke = ((strokeWidth || 0) / 3) * canvasZoom;

  if (outsideStroke > 0) {
    const c = hexToRgba(strokeColor, 1);
    effects.push(
      `${outsideStroke}px 0 0 ${c}`,
      `${-outsideStroke}px 0 0 ${c}`,
      `0 ${outsideStroke}px 0 ${c}`,
      `0 ${-outsideStroke}px 0 ${c}`,
      `${outsideStroke}px ${outsideStroke}px 0 ${c}`,
      `${outsideStroke}px ${-outsideStroke}px 0 ${c}`,
      `${-outsideStroke}px ${outsideStroke}px 0 ${c}`,
      `${-outsideStroke}px ${-outsideStroke}px 0 ${c}`
    );
  }

  return effects.length > 0 ? effects.join(", ") : "none";
};

const API_URL = "/api";

const CANVAS_PRESETS = [
  { name: "Portrait (9:16)", width: 1080, height: 1920 },
  { name: "Square (1:1)", width: 1080, height: 1080 },
  { name: "Landscape (16:9)", width: 1920, height: 1080 },
  { name: "Instagram Post (4:5)", width: 1080, height: 1350 },
];

const GOOGLE_FONTS = [
  "Arial",
  "Roboto",
  "Open Sans",
  "Montserrat",
  "Lato",
  "Oswald",
  "Raleway",
  "Poppins",
  "Playfair Display",
  "Merriweather",
  "Ubuntu",
  "Nunito",
  "PT Sans",
  "Bebas Neue",
  "Anton",
  "Pacifico",
  "Dancing Script",
  "Caveat",
  "Lobster",
  "Righteous",
  "Permanent Marker",
];

const QUICK_INSERT_EMOJIS = ["😃", "😂", "😉", "🥰", "😍", "🥳", "🥹", "😋", "😊", "😱", "🤤", "🤑"];
const COPIED_LAYER_STORAGE_KEY = "admin_template_editor_copied_layer";
const TIMELINE_SNAP_THRESHOLD_SECONDS = 0.2;

export default function AdminTemplateEditor() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const {
    currentTemplate,
    fetchTemplate,
    createTemplate,
    updateTemplate,
    addLayer,
    updateLayer,
    deleteLayer,
    setCurrentTemplate,
    isLoading,
  } = useTemplateStore();

  const [selectedLayer, setSelectedLayer] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showAddLayerDialog, setShowAddLayerDialog] = useState(false);
  const [showOtherVersionsPicker, setShowOtherVersionsPicker] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [newLayerType, setNewLayerType] = useState("image");
  const [newLayerName, setNewLayerName] = useState("");
  
  // Local text content state for fast typing without API lag
  const [localTextContent, setLocalTextContent] = useState("");
  const textUpdateTimerRef = useRef(null);
  const textContentInputRef = useRef(null);
  
  // Drag and resize state
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [layerStart, setLayerStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [showCenterGuides, setShowCenterGuides] = useState({ horizontal: false, vertical: false });
  const [showEdgeGuides, setShowEdgeGuides] = useState({ top: false, bottom: false, left: false, right: false });
  
  // Canvas zoom state
  const [canvasZoom, setCanvasZoom] = useState(1);
  
  // Layer reorder drag state
  const [draggedLayerId, setDraggedLayerId] = useState(null);
  const [dragOverLayerId, setDragOverLayerId] = useState(null);
  const [hasCopiedLayer, setHasCopiedLayer] = useState(false);
  const [timelineDrafts, setTimelineDrafts] = useState({});
  const [timelineDragState, setTimelineDragState] = useState(null);
  
  // Template settings form
  const [templateSettings, setTemplateSettings] = useState({
    name: "",
    description: "",
    canvas_width: 1080,
    canvas_height: 1920,
    duration: 15,
    sort_order: 1,
    category: "",
    language: "en",
    other_versions: [],
    is_public: false,
    thumbnail_url: "",
    preview_video_url: "",
  });

  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const videoRefs = useRef({});

  const isNewTemplate = !templateId;

  useEffect(() => {
    if (templateId) {
      fetchTemplate(templateId);
    } else {
      // Initialize new template
      setCurrentTemplate({
        name: "Untitled Template",
        description: "",
        canvas_width: 1080,
        canvas_height: 1920,
        duration: 15,
        language: "en",
        other_versions: [],
        layers: [],
        is_public: false,
      });
    }
  }, [templateId, fetchTemplate, setCurrentTemplate]);

  useEffect(() => {
    if (currentTemplate) {
      setTemplateSettings({
        name: currentTemplate.name || "Untitled Template",
        description: currentTemplate.description || "",
        canvas_width: currentTemplate.canvas_width || 1080,
        canvas_height: currentTemplate.canvas_height || 1920,
        duration: currentTemplate.duration || 15,
        sort_order: currentTemplate.sort_order || 1,
        category: currentTemplate.category || "",
        language: currentTemplate.language || "en",
        other_versions: currentTemplate.other_versions || [],
        is_public: currentTemplate.is_public || false,
        thumbnail_url: currentTemplate.thumbnail_url || "",
        preview_video_url: currentTemplate.preview_video_url || "",
      });
    }
  }, [currentTemplate]);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await axios.get("/templates");
        setAvailableTemplates(response.data || []);
      } catch (error) {
        console.error("Failed to load templates for version linking", error);
      }
    };

    loadTemplates();
  }, []);

  useEffect(() => {
    const existingCopy = localStorage.getItem(COPIED_LAYER_STORAGE_KEY);
    setHasCopiedLayer(Boolean(existingCopy));

    const handleStorage = (event) => {
      if (event.key === COPIED_LAYER_STORAGE_KEY) {
        setHasCopiedLayer(Boolean(event.newValue));
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const toggleOtherVersion = (versionId) => {
    const current = templateSettings.other_versions || [];
    const next = current.includes(versionId)
      ? current.filter((id) => id !== versionId)
      : [...current, versionId];

    setTemplateSettings({
      ...templateSettings,
      other_versions: next,
    });
  };

  // Sync selectedLayer with currentTemplate when layers update
  useEffect(() => {
    if (selectedLayer && currentTemplate?.layers) {
      const updatedLayer = currentTemplate.layers.find(l => l.id === selectedLayer.id);
      if (updatedLayer && JSON.stringify(updatedLayer) !== JSON.stringify(selectedLayer)) {
        setSelectedLayer(updatedLayer);
        // Update local text content when layer changes
        if (updatedLayer.type === "text") {
          setLocalTextContent(updatedLayer.content || "");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTemplate?.layers]);

  // Sync local text content when selectedLayer changes
  useEffect(() => {
    if (selectedLayer?.type === "text") {
      setLocalTextContent(selectedLayer.content || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLayer?.id]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (textUpdateTimerRef.current) {
        clearTimeout(textUpdateTimerRef.current);
      }
    };
  }, []);

  const syncPreviewVideos = useCallback((timelineTime, shouldPlay) => {
    if (!currentTemplate) return;

    Object.entries(videoRefs.current).forEach(([layerId, videoEl]) => {
      if (!videoEl) return;

      const layer = currentTemplate.layers?.find(l => l.id === layerId);
      if (!layer) return;

      const videoStartTime = timelineTime - layer.start_time;
      const layerDuration = Number(layer.duration) || 0;
      const isLayerActive = videoStartTime >= 0 && videoStartTime < layerDuration;

      videoEl.volume = Math.max(0, Math.min(1,
        (Number(layer.media_volume ?? 100) / 100) * getAnimationAudioGain(layer, videoStartTime)
      ));

      if (!isLayerActive) {
        videoEl.pause();
        return;
      }

      const rawSourceDuration = Number.isFinite(videoEl.duration) && videoEl.duration > 0
        ? videoEl.duration
        : Number(layer.source_duration || 0);
      const sourceDuration = Math.max(0, rawSourceDuration || layerDuration);
      const lastFrameTime = Math.max(0, sourceDuration - 0.05);
      const freezeDuration = Math.max(0, layerDuration - sourceDuration);
      let firstFreezeDuration = layer.freeze_first_frame ? freezeDuration : 0;

      if (layer.freeze_first_frame && layer.freeze_last_frame) {
        firstFreezeDuration = freezeDuration / 2;
      }

      const targetTime = Math.max(0, Math.min(videoStartTime - firstFreezeDuration, lastFrameTime));
      const hasManualTiming = layer.freeze_first_frame || layer.freeze_last_frame || freezeDuration > 0;
      const drift = Math.abs(videoEl.currentTime - targetTime);

      if (Number.isFinite(targetTime)) {
        if (!shouldPlay || videoEl.paused || hasManualTiming) {
          if (drift > 0.25) {
            videoEl.currentTime = targetTime;
          }
        } else if (drift > 0.75) {
          videoEl.currentTime = targetTime;
        }
      }

      const isHoldingFirstFrame = layer.freeze_first_frame && videoStartTime < firstFreezeDuration;
      const isHoldingLastFrame = freezeDuration > 0 && videoStartTime >= firstFreezeDuration + sourceDuration;
      if (isHoldingFirstFrame || isHoldingLastFrame) {
        if (drift > 0.05) {
          videoEl.currentTime = targetTime;
        }
        videoEl.pause();
      } else if (shouldPlay && videoEl.paused) {
        videoEl.play().catch(() => {});
      }
    });
  }, [currentTemplate]);

  useEffect(() => {
    if (isPlaying && currentTemplate) {
      const startTime = Date.now() - currentTime * 1000;
      syncPreviewVideos(currentTime, true);
      
      const animate = () => {
        const elapsed = (Date.now() - startTime) / 1000;
        if (elapsed >= currentTemplate.duration) {
          setCurrentTime(0);
          setIsPlaying(false);
          // Pause all videos
          Object.values(videoRefs.current).forEach(v => v?.pause());
        } else {
          syncPreviewVideos(elapsed, true);
          setCurrentTime(elapsed);
          animationRef.current = requestAnimationFrame(animate);
        }
      };
      
      animationRef.current = requestAnimationFrame(animate);
      
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        // Pause videos when stopping
        Object.values(videoRefs.current).forEach(v => v?.pause());
      };
    } else {
      // Pause all videos when not playing
      Object.values(videoRefs.current).forEach(v => v?.pause());
    }
  }, [isPlaying, currentTemplate, syncPreviewVideos]);

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      if (isNewTemplate) {
        const template = await createTemplate(templateSettings);
        if (template) {
          toast.success("Template created!");
          navigate(`/admin/templates/${template.id}/editor`, { replace: true });
        }
      } else {
        const result = await updateTemplate(templateId, templateSettings);
        if (result) {
          toast.success("Template saved!");
        }
      }
    } catch (error) {
      toast.error("Failed to save template");
    } finally {
      setIsSaving(false);
      setShowSettingsDialog(false);
    }
  };

  const handleAddLayer = async () => {
    if (!newLayerName.trim()) {
      toast.error("Please enter a layer name");
      return;
    }

    if (isNewTemplate) {
      // Need to save template first
      const template = await createTemplate(templateSettings);
      if (template) {
        navigate(`/admin/templates/${template.id}/editor`, { replace: true });
        toast.info("Template created. Now add your layers.");
      }
      return;
    }

    const layerData = {
      type: newLayerType,
      name: newLayerName,
      start_time: 0,
      duration: newLayerType === "custom_group" ? 4 : templateSettings.duration,
      default_group_duration: 4,
      position_x: 10,
      position_y: 10,
      width: 80,
      height: newLayerType === "text" ? 10 : newLayerType === "custom_group" ? 100 : 40,
      scale: 1,
      opacity: 1,
      rotation: 0,
      editable: true,
      stay_centered: true,
      locked: false,
      content: newLayerType === "text" ? "Sample Text" : null,
      media_url: null,
      media_type: null,
      media_fit: "cover",
      music_type: false,
      media_volume: 100,
      add_recording_capability: false,
      shapeType: "square",
      backgroundColor: "#14b8a6",
      backgroundOpacity: 1,
      borderColor: "#000000",
      borderWidth: 0,
      borderRadius: 0,
      topLeftOffsetX: 0,
      topLeftOffsetY: 0,
      topRightOffsetX: 0,
      topRightOffsetY: 0,
      bottomRightOffsetX: 0,
      bottomRightOffsetY: 0,
      bottomLeftOffsetX: 0,
      bottomLeftOffsetY: 0,
      entrance_easing: "linear",
      entrance_animation_duration: 0.5,
      exit_easing: "linear",
      exit_animation_duration: 0.5,

      pixverse_prompt: null,
      pixverse_duration: 5,          // ✅ add this
      pixverse_enabled: true,         // optional, but nice to define explicitly
      pixverse_reverse_clip: true,

      freeze_last_frame: false,
      freeze_first_frame: false,
      z_index: (currentTemplate?.layers?.length || 0) + 1,
    };

    const result = await addLayer(templateId, layerData);
    if (result) {
      toast.success("Layer added!");
      setSelectedLayer(result);
      setNewLayerName("");
      setShowAddLayerDialog(false);
    }
  };

  const handleUpdateLayer = async (layerId, updates) => {
    if (!templateId) return;
    console.log('Updating layer:', { layerId, updates });
    const updatedLayer = await updateLayer(templateId, layerId, updates);
    console.log('Layer updated:', updatedLayer);
    // Update selectedLayer if it's the one being updated
    if (selectedLayer?.id === layerId && updatedLayer) {
      setSelectedLayer(updatedLayer);
    }
  };

  // Debounced text update - only calls API after user stops typing
  const handleTextContentChange = (newContent) => {
    // Update local state immediately for fast UI response
    setLocalTextContent(newContent);
    
    // Clear previous timer
    if (textUpdateTimerRef.current) {
      clearTimeout(textUpdateTimerRef.current);
    }
    
    // Set new timer to update API after 500ms of no typing
    textUpdateTimerRef.current = setTimeout(() => {
      if (selectedLayer?.id) {
        handleUpdateLayer(selectedLayer.id, { content: newContent });
      }
    }, 500);
  };

  const handleInsertEmoji = (emoji) => {
    const textareaEl = textContentInputRef.current;
    const currentContent = localTextContent || "";
    const selectionStart = textareaEl?.selectionStart ?? currentContent.length;
    const selectionEnd = textareaEl?.selectionEnd ?? currentContent.length;
    const nextContent = `${currentContent.slice(0, selectionStart)}${emoji}${currentContent.slice(selectionEnd)}`;
    const nextCursorPos = selectionStart + emoji.length;

    handleTextContentChange(nextContent);

    requestAnimationFrame(() => {
      if (textareaEl) {
        textareaEl.focus();
        textareaEl.setSelectionRange(nextCursorPos, nextCursorPos);
      }
    });
  };

  const handleDeleteLayer = async (layerId) => {
    if (!templateId) return;
    const success = await deleteLayer(templateId, layerId);
    if (success) {
      toast.success("Layer deleted");
      if (selectedLayer?.id === layerId) {
        setSelectedLayer(null);
      }
    }
  };

  const handleCopyLayer = (layer) => {
    if (!layer) return;

    const {
      id,
      template_id,
      created_at,
      updated_at,
      ...copyableLayer
    } = layer;

    localStorage.setItem(COPIED_LAYER_STORAGE_KEY, JSON.stringify(copyableLayer));
    setHasCopiedLayer(true);
    toast.success(`Copied "${layer.name}"`);
  };

  const handlePasteLayer = async () => {
    if (!templateId) {
      toast.error("Please save the template first, then paste");
      return;
    }

    try {
      const copiedLayerRaw = localStorage.getItem(COPIED_LAYER_STORAGE_KEY);
      if (!copiedLayerRaw) {
        toast.error("No copied layer to paste");
        return;
      }

      const copiedLayer = JSON.parse(copiedLayerRaw);
      const templateDuration = templateSettings.duration || 15;
      const nextStartTime = Math.max(0, Math.min(copiedLayer.start_time || 0, templateDuration - 0.1));
      const requestedDuration = copiedLayer.duration || templateDuration;
      const nextDuration = Math.max(0.1, Math.min(requestedDuration, templateDuration - nextStartTime));

      const pastedLayer = await addLayer(templateId, {
        type: copiedLayer.type || "image",
        name: `${copiedLayer.name || "Layer"} Copy`,
        start_time: nextStartTime,
        duration: nextDuration,
        position_x: copiedLayer.position_x ?? 10,
        position_y: copiedLayer.position_y ?? 10,
        width: copiedLayer.width ?? 80,
        height: copiedLayer.height ?? 40,
        scale: copiedLayer.scale ?? 1,
        opacity: copiedLayer.opacity ?? 1,
        rotation: copiedLayer.rotation ?? 0,
        editable: copiedLayer.editable ?? true,
        locked: copiedLayer.locked ?? false,
        content: copiedLayer.content ?? null,
        media_url: copiedLayer.media_url ?? null,
        media_type: copiedLayer.media_type ?? null,
        media_fit: copiedLayer.media_fit ?? "cover",
        music_type: copiedLayer.music_type ?? false,
        media_volume: copiedLayer.media_volume ?? 100,
        add_recording_capability: copiedLayer.add_recording_capability ?? false,
        pixverse_prompt: copiedLayer.pixverse_prompt ?? null,
        pixverse_duration: copiedLayer.pixverse_duration ?? 5,
        pixverse_enabled: copiedLayer.pixverse_enabled ?? true,
        pixverse_reverse_clip: copiedLayer.pixverse_reverse_clip ?? true,
        freeze_last_frame: copiedLayer.freeze_last_frame ?? false,
        freeze_first_frame: copiedLayer.freeze_first_frame ?? false,
        z_index: (currentTemplate?.layers?.length || 0) + 1,
      });

      if (pastedLayer) {
        const restorableFields = { ...copiedLayer };
        delete restorableFields.name;
        delete restorableFields.start_time;
        delete restorableFields.duration;
        delete restorableFields.z_index;

        if (Object.keys(restorableFields).length > 0) {
          await updateLayer(templateId, pastedLayer.id, restorableFields);
        }

        await fetchTemplate(templateId);
        setSelectedLayer(pastedLayer);
        toast.success("Layer pasted");
      }
    } catch (error) {
      console.error("Failed to paste copied layer:", error);
      toast.error("Failed to paste layer");
    }
  };

  const handleMediaUpload = async (layerId, file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      // Backend returns "/api/files/{id}", but we need full URL without duplicating /api
      const mediaUrl = `${window.location.origin}${response.data.url}`;
      
      // Detect if uploaded file is audio, video, or image based on MIME type
      let mediaType = 'image';
      if (file.type.startsWith('audio/')) {
        mediaType = 'audio';
      } else if (file.type.startsWith('video/')) {
        mediaType = 'video';
      }
      
      const sourceDuration = await getLocalMediaDuration(file);

      console.log('Media uploaded:', { layerId, mediaUrl, mediaType, fileType: file.type, sourceDuration, response: response.data });

      const layerUpdate = {
        media_url: mediaUrl,
        media_type: mediaType,
      };

      if (sourceDuration && mediaType !== 'image') {
        layerUpdate.source_duration = Number(sourceDuration.toFixed(3));
      } else {
        layerUpdate.source_duration = null;
      }

      await handleUpdateLayer(layerId, layerUpdate);
      toast.success("Media uploaded!");
    } catch (error) {
      console.error('Media upload error:', error);
      toast.error("Failed to upload media");
    }
  };

  const handleMatchUploadedVideoDuration = async () => {
    if (!selectedLayer?.id || !selectedLayer.media_url) return;

    try {
      let sourceDuration = Number(selectedLayer.source_duration || 0);
      if (!Number.isFinite(sourceDuration) || sourceDuration <= 0) {
        sourceDuration = await getRemoteVideoDuration(selectedLayer.media_url);
      }

      if (!Number.isFinite(sourceDuration) || sourceDuration <= 0) {
        toast.error("Could not read uploaded video duration");
        return;
      }

      const matchedDuration = Number(sourceDuration.toFixed(3));
      await handleUpdateLayer(selectedLayer.id, {
        duration: matchedDuration,
        source_duration: matchedDuration,
      });
      toast.success(`Layer duration matched to ${matchedDuration}s`);
    } catch (error) {
      console.error('Failed to match video duration:', error);
      toast.error("Failed to match uploaded video duration");
    }
  };

  const handleRemoveMedia = async (layerId) => {
    try {
      await handleUpdateLayer(layerId, {
        media_url: null,
        media_type: null,
        source_duration: null,
      });
      toast.success("Media removed");
    } catch (error) {
      console.error('Media remove error:', error);
      toast.error("Failed to remove media");
    }
  };

  // Layer reorder handlers
  const handleLayerDragStart = (e, layerId) => {
    setDraggedLayerId(layerId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleLayerDragOver = (e, layerId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (draggedLayerId && draggedLayerId !== layerId) {
      setDragOverLayerId(layerId);
    }
  };

  const handleLayerDragLeave = () => {
    setDragOverLayerId(null);
  };

  const handleLayerDrop = async (e, targetLayerId) => {
    e.preventDefault();
    
    if (!draggedLayerId || draggedLayerId === targetLayerId || !currentTemplate) return;

    const layers = [...currentTemplate.layers];
    const draggedIndex = layers.findIndex(l => l.id === draggedLayerId);
    const targetIndex = layers.findIndex(l => l.id === targetLayerId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Remove dragged layer and insert at target position
    const [draggedLayer] = layers.splice(draggedIndex, 1);
    layers.splice(targetIndex, 0, draggedLayer);

    // Update z_index for all layers based on new order
    const updatedLayers = layers.map((layer, index) => ({
      ...layer,
      z_index: index
    }));

    // Update backend
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `/templates/${templateId}`,
        { layers: updatedLayers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh template
      await fetchTemplate(templateId);
      toast.success("Layer order updated");
    } catch (error) {
      console.error("Failed to update layer order:", error);
      toast.error("Failed to update layer order");
    }

    setDraggedLayerId(null);
    setDragOverLayerId(null);
  };

  const handleLayerDragEnd = () => {
    setDraggedLayerId(null);
    setDragOverLayerId(null);
  };

  const getLayerStyle = (layer) => {
    const isVisible = currentTime >= layer.start_time && currentTime < layer.start_time + layer.duration;
    
    return {
      position: "absolute",
      left: `${layer.position_x}%`,
      top: `${layer.position_y}%`,
      width: `${layer.width}%`,
      height: `${layer.height}%`,
      opacity: isVisible ? layer.opacity : 0,
      transform: `scale(${layer.scale}) rotate(${layer.rotation}deg)`,
      zIndex: layer.z_index,
      transition: "opacity 0.3s ease",
    };
  };

  // Drag and resize handlers
  const handleLayerMouseDown = (e, layer) => {
    if (e.target.classList.contains('resize-handle')) return; // Don't drag when clicking resize handle
    
    e.stopPropagation();
    setSelectedLayer(layer);
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setLayerStart({ 
      x: layer.position_x, 
      y: layer.position_y,
      width: layer.width,
      height: layer.height
    });
  };

  const handleResizeMouseDown = (e, layer, handle) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeHandle(handle);
    setDragStart({ x: e.clientX, y: e.clientY });
    setLayerStart({ 
      x: layer.position_x, 
      y: layer.position_y,
      width: layer.width,
      height: layer.height
    });
  };

  const handleMouseMove = (e) => {
    if (!selectedLayer || (!isDragging && !isResizing)) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const canvasRect = canvas.getBoundingClientRect();
    const deltaX = ((e.clientX - dragStart.x) / canvasRect.width) * 100;
    const deltaY = ((e.clientY - dragStart.y) / canvasRect.height) * 100;

    if (isDragging) {
      let newX = Math.max(0, Math.min(100 - layerStart.width, layerStart.x + deltaX));
      let newY = Math.max(0, Math.min(100 - layerStart.height, layerStart.y + deltaY));
      
      // Snap to center with threshold
      const snapThreshold = 2; // Snap when within 2% of center
      const centerX = 50 - (layerStart.width / 2); // Center position for the element
      const centerY = 50 - (layerStart.height / 2);
      
      // Check and apply horizontal snap
      const horizontalSnapped = Math.abs(newX - centerX) < snapThreshold;
      const verticalSnapped = Math.abs(newY - centerY) < snapThreshold;
      
      if (horizontalSnapped) {
        newX = centerX;
      }
      
      if (verticalSnapped) {
        newY = centerY;
      }
      
      // Update guide visibility
      setShowCenterGuides({
        horizontal: verticalSnapped,
        vertical: horizontalSnapped
      });
      
      handleUpdateLayer(selectedLayer.id, {
        position_x: newX,
        position_y: newY
      });
    } else if (isResizing) {
      const snapThreshold = 2; // Snap when within 2% of edge
      let updates = {};
      let edgeGuides = { top: false, bottom: false, left: false, right: false };
      
      switch (resizeHandle) {
        case 'top': {
          let newY = Math.max(0, layerStart.y + deltaY);
          let newHeight = Math.max(5, layerStart.height - deltaY);
          
          // Snap to top edge
          if (Math.abs(newY) < snapThreshold) {
            newY = 0;
            newHeight = layerStart.y + layerStart.height;
            edgeGuides.top = true;
          }
          
          updates = { position_y: newY, height: newHeight };
          break;
        }
        case 'bottom': {
          let newHeight = Math.max(5, Math.min(100 - layerStart.y, layerStart.height + deltaY));
          
          // Snap to bottom edge
          if (Math.abs((layerStart.y + newHeight) - 100) < snapThreshold) {
            newHeight = 100 - layerStart.y;
            edgeGuides.bottom = true;
          }
          
          updates = { height: newHeight };
          break;
        }
        case 'left': {
          let newX = Math.max(0, layerStart.x + deltaX);
          let newWidth = Math.max(5, layerStart.width - deltaX);
          
          // Snap to left edge
          if (Math.abs(newX) < snapThreshold) {
            newX = 0;
            newWidth = layerStart.x + layerStart.width;
            edgeGuides.left = true;
          }
          
          updates = { position_x: newX, width: newWidth };
          break;
        }
        case 'right': {
          let newWidth = Math.max(5, Math.min(100 - layerStart.x, layerStart.width + deltaX));
          
          // Snap to right edge
          if (Math.abs((layerStart.x + newWidth) - 100) < snapThreshold) {
            newWidth = 100 - layerStart.x;
            edgeGuides.right = true;
          }
          
          updates = { width: newWidth };
          break;
        }
        case 'top-left': {
          let newX = Math.max(0, layerStart.x + deltaX);
          let newY = Math.max(0, layerStart.y + deltaY);
          let newWidth = Math.max(5, layerStart.width - deltaX);
          let newHeight = Math.max(5, layerStart.height - deltaY);
          
          // Snap to left edge
          if (Math.abs(newX) < snapThreshold) {
            newX = 0;
            newWidth = layerStart.x + layerStart.width;
            edgeGuides.left = true;
          }
          
          // Snap to top edge
          if (Math.abs(newY) < snapThreshold) {
            newY = 0;
            newHeight = layerStart.y + layerStart.height;
            edgeGuides.top = true;
          }
          
          updates = { position_x: newX, position_y: newY, width: newWidth, height: newHeight };
          break;
        }
        case 'top-right': {
          let newY = Math.max(0, layerStart.y + deltaY);
          let newWidth = Math.max(5, Math.min(100 - layerStart.x, layerStart.width + deltaX));
          let newHeight = Math.max(5, layerStart.height - deltaY);
          
          // Snap to top edge
          if (Math.abs(newY) < snapThreshold) {
            newY = 0;
            newHeight = layerStart.y + layerStart.height;
            edgeGuides.top = true;
          }
          
          // Snap to right edge
          if (Math.abs((layerStart.x + newWidth) - 100) < snapThreshold) {
            newWidth = 100 - layerStart.x;
            edgeGuides.right = true;
          }
          
          updates = { position_y: newY, width: newWidth, height: newHeight };
          break;
        }
        case 'bottom-left': {
          let newX = Math.max(0, layerStart.x + deltaX);
          let newWidth = Math.max(5, layerStart.width - deltaX);
          let newHeight = Math.max(5, Math.min(100 - layerStart.y, layerStart.height + deltaY));
          
          // Snap to left edge
          if (Math.abs(newX) < snapThreshold) {
            newX = 0;
            newWidth = layerStart.x + layerStart.width;
            edgeGuides.left = true;
          }
          
          // Snap to bottom edge
          if (Math.abs((layerStart.y + newHeight) - 100) < snapThreshold) {
            newHeight = 100 - layerStart.y;
            edgeGuides.bottom = true;
          }
          
          updates = { position_x: newX, width: newWidth, height: newHeight };
          break;
        }
        case 'bottom-right': {
          let newWidth = Math.max(5, Math.min(100 - layerStart.x, layerStart.width + deltaX));
          let newHeight = Math.max(5, Math.min(100 - layerStart.y, layerStart.height + deltaY));
          
          // Snap to right edge
          if (Math.abs((layerStart.x + newWidth) - 100) < snapThreshold) {
            newWidth = 100 - layerStart.x;
            edgeGuides.right = true;
          }
          
          // Snap to bottom edge
          if (Math.abs((layerStart.y + newHeight) - 100) < snapThreshold) {
            newHeight = 100 - layerStart.y;
            edgeGuides.bottom = true;
          }
          
          updates = { width: newWidth, height: newHeight };
          break;
        }
      }
      
      setShowEdgeGuides(edgeGuides);
      handleUpdateLayer(selectedLayer.id, updates);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
    setShowCenterGuides({ horizontal: false, vertical: false });
    setShowEdgeGuides({ top: false, bottom: false, left: false, right: false });
  };

  // Add mouse event listeners
  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, isResizing, dragStart, layerStart, selectedLayer]);

  const getClipStyle = (layer) => {
    const draft = timelineDrafts[layer.id];
    const startTime = draft?.start_time ?? layer.start_time;
    const layerDuration = draft?.duration ?? layer.duration;
    const duration = currentTemplate?.duration || 15;
    const startPercent = (startTime / duration) * 100;
    const widthPercent = (layerDuration / duration) * 100;
    
    return {
      left: `${startPercent}%`,
      width: `${widthPercent}%`,
    };
  };

  const handleTimelineMouseDown = (e, layer, mode) => {
    e.preventDefault();
    e.stopPropagation();

    const trackElement = e.currentTarget.closest(".timeline-layer-track");
    if (!trackElement) return;

    const trackRect = trackElement.getBoundingClientRect();
    setSelectedLayer(layer);
    setTimelineDragState({
      layerId: layer.id,
      mode,
      startClientX: e.clientX,
      trackWidth: trackRect.width,
      originalStart: layer.start_time,
      originalDuration: layer.duration,
      templateDuration: currentTemplate?.duration || templateSettings.duration || 15,
      didDrag: false,
    });
  };

  useEffect(() => {
    if (!timelineDragState) return;

    const getSnapPoints = () => {
      const layers = currentTemplate?.layers || [];
      return layers
        .filter((layer) => layer.id !== timelineDragState.layerId)
        .flatMap((layer) => {
          const start = Number(layer.start_time) || 0;
          const end = start + (Number(layer.duration) || 0);
          return [start, end];
        });
    };

    const snapToPoints = (value, snapPoints) => {
      if (!snapPoints.length) return value;
      let closest = value;
      let minDistance = Number.POSITIVE_INFINITY;
      snapPoints.forEach((point) => {
        const distance = Math.abs(point - value);
        if (distance < minDistance) {
          minDistance = distance;
          closest = point;
        }
      });
      return minDistance <= TIMELINE_SNAP_THRESHOLD_SECONDS ? closest : value;
    };

    const handleTimelineMouseMove = (e) => {
      const deltaPixels = e.clientX - timelineDragState.startClientX;
      const secondsPerPixel = timelineDragState.templateDuration / Math.max(timelineDragState.trackWidth, 1);
      const deltaSeconds = deltaPixels * secondsPerPixel;
      const snapPoints = getSnapPoints();

      let nextStart = timelineDragState.originalStart;
      let nextDuration = timelineDragState.originalDuration;

      if (timelineDragState.mode === "move") {
        const unsnappedStart = Math.max(
          0,
          Math.min(
            timelineDragState.originalStart + deltaSeconds,
            timelineDragState.templateDuration - timelineDragState.originalDuration
          )
        );
        const snappedStart = snapToPoints(unsnappedStart, snapPoints);
        const currentEnd = unsnappedStart + timelineDragState.originalDuration;
        const snappedEnd = snapToPoints(currentEnd, snapPoints);
        const endSnappedStart = snappedEnd - timelineDragState.originalDuration;
        nextStart = Math.max(
          0,
          Math.min(
            Math.abs(snappedStart - unsnappedStart) <= Math.abs(endSnappedStart - unsnappedStart)
              ? snappedStart
              : endSnappedStart,
            timelineDragState.templateDuration - timelineDragState.originalDuration
          )
        );
      } else if (timelineDragState.mode === "resize-left") {
        const maxLeft = timelineDragState.originalStart + timelineDragState.originalDuration - 0.1;
        const unsnappedStart = Math.max(0, Math.min(timelineDragState.originalStart + deltaSeconds, maxLeft));
        nextStart = Math.max(0, Math.min(snapToPoints(unsnappedStart, snapPoints), maxLeft));
        nextDuration = Math.max(0.1, timelineDragState.originalDuration - (nextStart - timelineDragState.originalStart));
      } else if (timelineDragState.mode === "resize-right") {
        const unsnappedDuration = Math.max(
          0.1,
          Math.min(
            timelineDragState.originalDuration + deltaSeconds,
            timelineDragState.templateDuration - timelineDragState.originalStart
          )
        );
        const unsnappedEnd = timelineDragState.originalStart + unsnappedDuration;
        const snappedEnd = Math.max(
          timelineDragState.originalStart + 0.1,
          Math.min(
            snapToPoints(unsnappedEnd, snapPoints),
            timelineDragState.templateDuration
          )
        );
        nextDuration = snappedEnd - timelineDragState.originalStart;
      }

      setTimelineDrafts((prev) => ({
        ...prev,
        [timelineDragState.layerId]: {
          start_time: Number(nextStart.toFixed(2)),
          duration: Number(nextDuration.toFixed(2)),
        },
      }));

      setTimelineDragState((prev) => (prev ? { ...prev, didDrag: true } : prev));
    };

    const handleTimelineMouseUp = () => {
      const draft = timelineDrafts[timelineDragState.layerId];
      if (draft && timelineDragState.didDrag) {
        handleUpdateLayer(timelineDragState.layerId, draft);
      }
      setTimelineDragState(null);
      setTimeout(() => {
        setTimelineDrafts((prev) => {
          const next = { ...prev };
          delete next[timelineDragState.layerId];
          return next;
        });
      }, 0);
    };

    window.addEventListener("mousemove", handleTimelineMouseMove);
    window.addEventListener("mouseup", handleTimelineMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleTimelineMouseMove);
      window.removeEventListener("mouseup", handleTimelineMouseUp);
    };
  }, [timelineDragState, timelineDrafts, currentTemplate?.layers, templateSettings.duration, templateId, updateLayer, fetchTemplate]);

  if (isLoading && !currentTemplate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Toolbar */}
      <header className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin/templates")} className="text-white hover:bg-white/10">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-semibold">{templateSettings.name}</h1>
            <p className="text-sm text-white/60">
              {templateSettings.canvas_width}×{templateSettings.canvas_height} • {templateSettings.duration}s
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
            <DialogTrigger asChild>
              <Button variant="ghost" className="text-white hover:bg-white/10">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            </DialogTrigger>
            <DialogContent className="flex max-h-[calc(100vh-2rem)] max-h-[calc(100dvh-2rem)] max-w-md flex-col bg-slate-50">
              <DialogHeader>
                <DialogTitle>Template Settings</DialogTitle>
              </DialogHeader>
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto py-4 pr-2">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={templateSettings.name}
                    onChange={(e) => setTemplateSettings({ ...templateSettings, name: e.target.value })}
                    data-testid="template-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={templateSettings.description}
                    onChange={(e) => setTemplateSettings({ ...templateSettings, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Canvas Size</Label>
                  <Select
                    value={`${templateSettings.canvas_width}x${templateSettings.canvas_height}`}
                    onValueChange={(v) => {
                      const [w, h] = v.split("x").map(Number);
                      setTemplateSettings({ ...templateSettings, canvas_width: w, canvas_height: h });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CANVAS_PRESETS.map((preset) => (
                        <SelectItem key={preset.name} value={`${preset.width}x${preset.height}`}>
                          {preset.name} ({preset.width}×{preset.height})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Duration (seconds)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={60}
                    value={templateSettings.duration}
                    onChange={(e) => setTemplateSettings({ ...templateSettings, duration: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Order</Label>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={templateSettings.sort_order}
                    onChange={(e) => setTemplateSettings({ ...templateSettings, sort_order: Number(e.target.value) || 1 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={templateSettings.category}
                    onValueChange={(v) => setTemplateSettings({ ...templateSettings, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="social">Transform Image to video with AI</SelectItem>
                      <SelectItem value="product">Simple & Visual video templates</SelectItem>
                      <SelectItem value="edited">Simple edited video templates</SelectItem>
                      <SelectItem value="marketing">Fun & Creative video templates</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Language</Label>
                  <Select
                    value={templateSettings.language || "en"}
                    onValueChange={(v) => setTemplateSettings({ ...templateSettings, language: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                      <SelectItem value="es">Spanish</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label>Other Versions</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowOtherVersionsPicker((prev) => !prev)}
                    >
                      {showOtherVersionsPicker ? "Hide versions" : "Add versions"}
                    </Button>
                  </div>
                  {!!templateSettings.other_versions?.length && (
                    <p className="text-xs text-slate-600">
                      {templateSettings.other_versions.length} linked version(s)
                    </p>
                  )}
                  {showOtherVersionsPicker && (
                    <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 p-2 space-y-2 bg-white">
                      {availableTemplates
                        .filter((template) => template.id !== templateId)
                        .map((template) => (
                          <label key={template.id} className="flex items-start gap-2 cursor-pointer">
                            <Checkbox
                              checked={(templateSettings.other_versions || []).includes(template.id)}
                              onCheckedChange={() => toggleOtherVersion(template.id)}
                            />
                            <span className="text-sm leading-tight">
                              {template.name}
                              <span className="block text-xs text-slate-500">
                                {template.canvas_width}×{template.canvas_height} • {template.duration}s • {template.language || "en"}
                              </span>
                            </span>
                          </label>
                        ))}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Thumbnail</Label>
                  <div className="flex gap-2">
                    <Input
                      value={templateSettings.thumbnail_url}
                      onChange={(e) => setTemplateSettings({ ...templateSettings, thumbnail_url: e.target.value })}
                      placeholder="Enter thumbnail URL or upload"
                      className="flex-1"
                    />
                  </div>
                  <label className="block cursor-pointer">
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center hover:border-teal-300 transition-colors">
                      <Upload className="w-5 h-5 mx-auto mb-1 text-slate-400" />
                      <span className="text-xs text-slate-600">Upload thumbnail image</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        if (e.target.files?.[0]) {
                          try {
                            const formData = new FormData();
                            formData.append("file", e.target.files[0]);
                            const response = await axios.post("/upload", formData, {
                              headers: { "Content-Type": "multipart/form-data" },
                            });
                            const thumbnailUrl = `${window.location.origin}${response.data.url}`;
                            console.log('Thumbnail uploaded:', { thumbnailUrl, response: response.data });
                            setTemplateSettings({ ...templateSettings, thumbnail_url: thumbnailUrl });
                            toast.success("Thumbnail uploaded!");
                          } catch (error) {
                            console.error('Thumbnail upload error:', error);
                            toast.error("Failed to upload thumbnail");
                          }
                        }
                      }}
                    />
                  </label>
                  {templateSettings.thumbnail_url && (
                    <div className="mt-2">
                      <img
                        src={templateSettings.thumbnail_url}
                        alt="Thumbnail preview"
                        className="w-full h-24 object-cover rounded-lg border border-slate-200"
                      />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Thumbnail preview video (.mp4)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={templateSettings.preview_video_url}
                      onChange={(e) => setTemplateSettings({ ...templateSettings, preview_video_url: e.target.value })}
                      placeholder="Enter .mp4 URL or upload"
                      className="flex-1"
                    />
                  </div>
                  <label className="block cursor-pointer">
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center hover:border-teal-300 transition-colors">
                      <Upload className="w-5 h-5 mx-auto mb-1 text-slate-400" />
                      <span className="text-xs text-slate-600">Upload thumbnail preview video</span>
                    </div>
                    <input
                      type="file"
                      accept="video/mp4,.mp4"
                      className="hidden"
                      onChange={async (e) => {
                        if (e.target.files?.[0]) {
                          try {
                            const formData = new FormData();
                            formData.append("file", e.target.files[0]);
                            const response = await axios.post("/upload", formData, {
                              headers: { "Content-Type": "multipart/form-data" },
                            });
                            const previewVideoUrl = `${window.location.origin}${response.data.url}`;
                            console.log('Thumbnail preview video uploaded:', { previewVideoUrl, response: response.data });
                            setTemplateSettings({ ...templateSettings, preview_video_url: previewVideoUrl });
                            toast.success("Preview video uploaded!");
                          } catch (error) {
                            console.error('Preview video upload error:', error);
                            toast.error("Failed to upload preview video");
                          }
                        }
                      }}
                    />
                  </label>
                  {templateSettings.preview_video_url && (
                    <div className="mt-2">
                      <video
                        src={templateSettings.preview_video_url}
                        className="w-full h-24 object-cover rounded-lg border border-slate-200"
                        controls
                        muted
                        playsInline
                        preload="metadata"
                      />
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <Label>Make Public</Label>
                  <Switch
                    checked={templateSettings.is_public}
                    onCheckedChange={(v) => setTemplateSettings({ ...templateSettings, is_public: v })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveSettings} disabled={isSaving} className="btn-primary">
                  {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Button
            onClick={handleSaveSettings}
            disabled={isSaving}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="save-template-btn"
          >
            {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </header>

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Layer Panel */}
        <div className="w-full md:w-64 bg-white border-b md:border-b-0 md:border-r border-slate-200 flex flex-col max-h-64 md:max-h-none">
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Layers</h3>
              <div className="flex items-center gap-1">
                {hasCopiedLayer && (
                  <Button size="sm" variant="ghost" onClick={handlePasteLayer}>
                    <ClipboardPaste className="w-4 h-4 mr-1" />
                    Paste
                  </Button>
                )}
                <Dialog open={showAddLayerDialog} onOpenChange={setShowAddLayerDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="ghost">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Layer</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Layer Type</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                        {[
                          { type: "image", icon: Image, label: "AI Image" },
                          { type: "video", icon: Video, label: "Media" },
                          { type: "text", icon: Type, label: "Text" },
                          { type: "shape", icon: Square, label: "Shape" },
                          { type: "custom_group", icon: Layers, label: "Custom Group" },
                        ].map(({ type, icon: Icon, label }) => (
                          <button
                            key={type}
                            className={`p-4 rounded-lg border-2 transition-colors ${
                              newLayerType === type
                                ? "border-violet-500 bg-violet-50"
                                : "border-slate-200 hover:border-slate-300"
                            }`}
                            onClick={() => setNewLayerType(type)}
                          >
                            <Icon className={`w-6 h-6 mx-auto mb-1 ${newLayerType === type ? "text-teal-600" : "text-slate-400"}`} />
                            <span className="text-xs">{label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Layer Name</Label>
                      <Input
                        value={newLayerName}
                        onChange={(e) => setNewLayerName(e.target.value)}
                        placeholder={`My ${newLayerType}`}
                        data-testid="new-layer-name"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowAddLayerDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleAddLayer} className="btn-primary" data-testid="add-layer-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Layer
                    </Button>
                  </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            {currentTemplate?.layers?.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No layers yet</p>
                <p className="text-xs">Click + to add a layer</p>
              </div>
            ) : (
              <div className="space-y-1">
                {currentTemplate?.layers?.slice().reverse().map((layer) => (
                  <div
                    key={layer.id}
                    draggable
                    onDragStart={(e) => handleLayerDragStart(e, layer.id)}
                    onDragOver={(e) => handleLayerDragOver(e, layer.id)}
                    onDragLeave={handleLayerDragLeave}
                    onDrop={(e) => handleLayerDrop(e, layer.id)}
                    onDragEnd={handleLayerDragEnd}
                    className={`w-full text-left p-3 rounded-lg transition-colors cursor-move ${
                      selectedLayer?.id === layer.id
                        ? "bg-teal-100 text-teal-700"
                        : dragOverLayerId === layer.id
                        ? "bg-blue-100 border-2 border-blue-400"
                        : draggedLayerId === layer.id
                        ? "opacity-50"
                        : "hover:bg-slate-100"
                    }`}
                    onClick={() => setSelectedLayer(layer)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedLayer(layer);
                      }
                    }}
                    data-testid={`layer-item-${layer.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-slate-400 cursor-grab active:cursor-grabbing" />
                      {layer.type === "image" && <Image className="w-4 h-4" />}
                      {layer.type === "video" && <Video className="w-4 h-4" />}
                      {layer.type === "text" && <Type className="w-4 h-4" />}
                      {layer.type === "shape" && <Square className="w-4 h-4" />}
                      {layer.type === "custom_group" && <Layers className="w-4 h-4" />}
                      <span className="flex-1 truncate text-sm">{layer.name}</span>
                      {layer.editable ? (
                        <Unlock className="w-3 h-3 text-green-500" />
                      ) : (
                        <Lock className="w-3 h-3 text-slate-400" />
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyLayer(layer);
                        }}
                        title="Copy layer"
                      >
                        <Copy className="w-3 h-3 text-slate-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Canvas Area */}
        <div className="flex-1 flex flex-col order-2 md:order-none">
          <div 
            className="flex-1 p-8 overflow-auto bg-slate-200/50 relative"
            onClick={() => setSelectedLayer(null)}
          >
            <div className="flex items-center justify-center h-full">
              <div
                ref={canvasRef}
                className="relative bg-white shadow-2xl rounded-lg overflow-hidden"
                style={{
                  width: Math.min(400, templateSettings.canvas_width / 3) * canvasZoom,
                  height: Math.min(600, templateSettings.canvas_height / 3) * canvasZoom,
                  aspectRatio: `${templateSettings.canvas_width} / ${templateSettings.canvas_height}`,
                }}
                data-testid="editor-canvas"
                onClick={(e) => {
                  // If clicking directly on canvas background (not a layer), deselect
                  if (e.target === e.currentTarget) {
                    setSelectedLayer(null);
                  }
                }}
              >
                {/* Render layers */}
                {currentTemplate?.layers?.map((layer) => {
                  const mediaFit = layer.media_fit || "cover";
                  const animationStyle = getLayerAnimationStyle(
                    layer, 
                    currentTime, 
                    isPlaying,
                    templateSettings.canvas_width,
                    templateSettings.canvas_height
                  );
                  
                  return (
                  <div
                    key={layer.id}
                    className={`absolute ${
                      selectedLayer?.id === layer.id ? "ring-2 ring-teal-500" : ""
                    } ${isDragging || isResizing ? "cursor-move" : "cursor-pointer"}`}
                    style={{
                      ...getLayerStyle(layer),
                      ...animationStyle,
                      ...(layer.music_type ? { opacity: 0, pointerEvents: 'none' } : {}),
                      transition: isPlaying ? 'none' : 'all 0.2s',
                      ...((["image", "video"].includes(layer.type)) ? getMediaCornerRadiusStyle(layer, (canvasZoom || 1) / 3) : {})
                    }}
                    onMouseDown={(e) => handleLayerMouseDown(e, layer)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedLayer(layer);
                    }}
                  >
                    {layer.type === "shape" && <ShapeLayer layer={layer} scale={(canvasZoom || 1) / 3} />}
                    {layer.type === "video" && layer.media_url && (
                      <>
                        {/* Render based on stored media_type (video, audio, or image) */}
                        {layer.media_type === 'audio' ? (
                          layer.music_type ? (
                            <audio
                              ref={el => { if (el) videoRefs.current[layer.id] = el; }}
                              src={layer.media_url}
                              preload="metadata"
                            />
                          ) : (
                            <div className="w-full h-full bg-slate-900/90 text-white flex items-center justify-center p-2">
                              <div className="text-center space-y-2">
                                <Music className="w-8 h-8 mx-auto text-teal-300" />
                                <p className="text-xs font-medium truncate">{layer.name || "Audio"}</p>
                                <audio
                                  ref={el => { if (el) videoRefs.current[layer.id] = el; }}
                                  src={layer.media_url}
                                  controls
                                  preload="metadata"
                                  className="w-full max-w-[220px]"
                                />
                              </div>
                            </div>
                          )
                        ) : (layer.media_type === 'video' || !layer.media_type) ? (
                          <video 
                            ref={el => { if (el) videoRefs.current[layer.id] = el; }}
                            src={layer.media_url}
                            className="w-full h-full" 
                            style={{
                              objectFit: mediaFit,
                              objectPosition: `${layer.objectPositionX || 50}% ${layer.objectPositionY || 50}%`,
                              pointerEvents: 'none'
                            }}
                            playsInline
                            loop={false}
                          />
                        ) : (
                          <img
                            src={layer.media_url}
                            alt={layer.name}
                            className="w-full h-full pointer-events-none"
                            style={{
                              objectFit: mediaFit,
                              objectPosition: `${layer.objectPositionX || 50}% ${layer.objectPositionY || 50}%`
                            }}
                          />
                        )}
                      </>
                    )}

                    {layer.type === "image" && layer.media_url && (
                      <img
                        src={layer.media_url}
                        alt={layer.name}
                        className="w-full h-full pointer-events-none"
                        style={{
                          objectFit: mediaFit,
                          objectPosition: `${layer.objectPositionX || 50}% ${layer.objectPositionY || 50}%`
                        }}
                      />
                    )}
                    {layer.type === "text" && (
                      <TextWipeLayer
                        progress={animationStyle["--text-wipe-progress"]}
                        isExit={animationStyle["--text-wipe-exit"] === 1}
                        className="flex items-center justify-center h-full whitespace-pre-wrap overflow-hidden pointer-events-none"
                        style={{
                          fontFamily: layer.fontFamily || "Arial",
                          fontSize: `${calculateAutoFontSize(
                            (layer.width / 100) * (currentTemplate?.canvas_width || 1080),
                            (layer.height / 100) * (currentTemplate?.canvas_height || 1920),
                            selectedLayer?.id === layer.id ? localTextContent : (layer.content || "Sample Text"),
                            {
                              lineHeight: layer.lineHeight || 1.5,
                              fontFamily: layer.fontFamily || "Arial",
                              fontWeight: layer.fontWeight || "normal",
                              fontStyle: layer.fontStyle || "normal"
                            }
                          ) / 3 * canvasZoom}px`,  // Divide by 3 for canvas scale, multiply by zoom
                          fontWeight: layer.fontWeight || "normal",
                          fontStyle: layer.fontStyle || "normal",
                          color: layer.textColor || "#000000",
                          textShadow: buildTextEffectShadow({
                            strokeWidth: layer.textStrokeWidth || 0,
                            strokeColor: layer.textStrokeColor || "#000000",
                            canvasZoom,
                          }),
                          backgroundColor: layer.backgroundColor ? `${layer.backgroundColor}${Math.round((layer.backgroundOpacity || 0) * 255).toString(16).padStart(2, '0')}` : "transparent",
                          textAlign: layer.textAlign || "center",
                          lineHeight: layer.lineHeight || 1.5,
                          justifyContent: layer.textAlign === "left" ? "flex-start" : layer.textAlign === "right" ? "flex-end" : "center",
                          borderRadius: `${((layer.borderRadius || 0) / 3) * canvasZoom}px`,
                          padding: `${(getBackendTextMetrics(
                            (layer.width / 100) * (currentTemplate?.canvas_width || 1080),
                            (layer.height / 100) * (currentTemplate?.canvas_height || 1920),
                            layer
                          ).paddingV / 3) * canvasZoom}px ${(getBackendTextMetrics(
                            (layer.width / 100) * (currentTemplate?.canvas_width || 1080),
                            (layer.height / 100) * (currentTemplate?.canvas_height || 1920),
                            layer
                          ).paddingH / 3) * canvasZoom}px`,
                          display: "flex",
                          alignItems: "center",
                          border: layer.borderWidth ? `${(layer.borderWidth / 3) * canvasZoom}px solid ${layer.borderColor || "#000000"}` : "none",
                        }}
                      >
                          {selectedLayer?.id === layer.id ? localTextContent : (layer.content || "Sample Text")}
                      </TextWipeLayer>
                    )}
                    {(layer.type === "image" || layer.type === "video") && !layer.media_url && (
                      <div className="w-full h-full bg-slate-100 flex items-center justify-center pointer-events-none">
                        {layer.type === "image" ? (
                          <Image className="w-8 h-8 text-slate-300" />
                        ) : (
                          <Video className="w-8 h-8 text-slate-300" />
                        )}
                      </div>
                    )}
                    
                    {/* Resize Handles - only show for selected layer */}
                    {selectedLayer?.id === layer.id && (
                      <>
                        {/* Corner handles */}
                        <div 
                          className="resize-handle absolute bg-teal-500 border border-white rounded-full cursor-nwse-resize z-10"
                          style={{
                            top: '-4px',
                            left: '-4px',
                            width: `${12 / canvasZoom}px`,
                            height: `${12 / canvasZoom}px`,
                          }}
                          onMouseDown={(e) => handleResizeMouseDown(e, layer, 'top-left')}
                        />
                        <div 
                          className="resize-handle absolute bg-teal-500 border border-white rounded-full cursor-nesw-resize z-10"
                          style={{
                            top: '-4px',
                            right: '-4px',
                            width: `${12 / canvasZoom}px`,
                            height: `${12 / canvasZoom}px`,
                          }}
                          onMouseDown={(e) => handleResizeMouseDown(e, layer, 'top-right')}
                        />
                        <div 
                          className="resize-handle absolute bg-teal-500 border border-white rounded-full cursor-nesw-resize z-10"
                          style={{
                            bottom: '-4px',
                            left: '-4px',
                            width: `${12 / canvasZoom}px`,
                            height: `${12 / canvasZoom}px`,
                          }}
                          onMouseDown={(e) => handleResizeMouseDown(e, layer, 'bottom-left')}
                        />
                        <div 
                          className="resize-handle absolute bg-teal-500 border border-white rounded-full cursor-nwse-resize z-10"
                          style={{
                            bottom: '-4px',
                            right: '-4px',
                            width: `${12 / canvasZoom}px`,
                            height: `${12 / canvasZoom}px`,
                          }}
                          onMouseDown={(e) => handleResizeMouseDown(e, layer, 'bottom-right')}
                        />
                        
                        {/* Side handles */}
                        <div 
                          className="resize-handle absolute left-1/2 -translate-x-1/2 bg-teal-500 border border-white rounded-full cursor-ns-resize z-10"
                          style={{
                            top: '-4px',
                            width: `${12 / canvasZoom}px`,
                            height: `${12 / canvasZoom}px`,
                          }}
                          onMouseDown={(e) => handleResizeMouseDown(e, layer, 'top')}
                        />
                        <div 
                          className="resize-handle absolute left-1/2 -translate-x-1/2 bg-teal-500 border border-white rounded-full cursor-ns-resize z-10"
                          style={{
                            bottom: '-4px',
                            width: `${12 / canvasZoom}px`,
                            height: `${12 / canvasZoom}px`,
                          }}
                          onMouseDown={(e) => handleResizeMouseDown(e, layer, 'bottom')}
                        />
                        <div 
                          className="resize-handle absolute top-1/2 -translate-y-1/2 bg-teal-500 border border-white rounded-full cursor-ew-resize z-10"
                          style={{
                            left: '-4px',
                            width: `${12 / canvasZoom}px`,
                            height: `${12 / canvasZoom}px`,
                          }}
                          onMouseDown={(e) => handleResizeMouseDown(e, layer, 'left')}
                        />
                        <div 
                          className="resize-handle absolute top-1/2 -translate-y-1/2 bg-teal-500 border border-white rounded-full cursor-ew-resize z-10"
                          style={{
                            right: '-4px',
                            width: `${12 / canvasZoom}px`,
                            height: `${12 / canvasZoom}px`,
                          }}
                          onMouseDown={(e) => handleResizeMouseDown(e, layer, 'right')}
                        />
                      </>
                    )}
                  </div>
                );
                })}
                
                {/* Center Guide Lines */}
                {showCenterGuides.vertical && (
                  <div 
                    className="absolute top-0 bottom-0 w-0.5 bg-teal-500 pointer-events-none z-50"
                    style={{ left: '50%', transform: 'translateX(-50%)' }}
                  >
                    <div className="absolute top-1/2 -translate-y-1/2 -left-8 bg-teal-500 text-white text-xs px-2 py-0.5 rounded">
                      Center
                    </div>
                  </div>
                )}
                {showCenterGuides.horizontal && (
                  <div 
                    className="absolute left-0 right-0 h-0.5 bg-teal-500 pointer-events-none z-50"
                    style={{ top: '50%', transform: 'translateY(-50%)' }}
                  >
                    <div className="absolute left-1/2 -translate-x-1/2 -top-6 bg-teal-500 text-white text-xs px-2 py-0.5 rounded">
                      Center
                    </div>
                  </div>
                )}
                
                {/* Edge Guide Lines */}
                {showEdgeGuides.left && (
                  <div 
                    className="absolute top-0 bottom-0 left-0 w-0.5 bg-orange-500 pointer-events-none z-50"
                  >
                    <div className="absolute top-1/2 -translate-y-1/2 left-1 bg-orange-500 text-white text-xs px-2 py-0.5 rounded">
                      Left
                    </div>
                  </div>
                )}
                {showEdgeGuides.right && (
                  <div 
                    className="absolute top-0 bottom-0 right-0 w-0.5 bg-orange-500 pointer-events-none z-50"
                  >
                    <div className="absolute top-1/2 -translate-y-1/2 right-1 bg-orange-500 text-white text-xs px-2 py-0.5 rounded">
                      Right
                    </div>
                  </div>
                )}
                {showEdgeGuides.top && (
                  <div 
                    className="absolute top-0 left-0 right-0 h-0.5 bg-orange-500 pointer-events-none z-50"
                  >
                    <div className="absolute top-1 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded">
                      Top
                    </div>
                  </div>
                )}
                {showEdgeGuides.bottom && (
                  <div 
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500 pointer-events-none z-50"
                  >
                    <div className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded">
                      Bottom
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Canvas Zoom Control */}
            <div className="absolute bottom-4 right-4 bg-white shadow-lg rounded-lg p-3 border border-slate-200">
              <div className="flex items-center gap-3">
                <Label className="text-xs font-medium text-slate-700">Zoom</Label>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => setCanvasZoom(Math.max(0.25, canvasZoom - 0.25))}
                  >
                    -
                  </Button>
                  <span className="text-xs font-mono text-slate-600 w-12 text-center">
                    {(canvasZoom * 100).toFixed(0)}%
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => setCanvasZoom(Math.min(3, canvasZoom + 0.25))}
                  >
                    +
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setCanvasZoom(1)}
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="timeline-container bg-white border-t border-slate-200">
            <div className="flex items-center gap-4 mb-4">
              <Button size="sm" variant="outline" onClick={() => setIsPlaying(!isPlaying)}>
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </Button>
              <span className="text-sm font-mono text-slate-600">
                {currentTime.toFixed(1)}s / {templateSettings.duration}s
              </span>
              <Slider
                value={[currentTime]}
                max={templateSettings.duration}
                step={0.1}
                onValueChange={([val]) => setCurrentTime(val)}
                className="flex-1"
              />
            </div>

            <div className="space-y-1">
              {currentTemplate?.layers?.slice().reverse().map((layer) => (
                <div key={layer.id} className="timeline-layer-row">
                  <div className="timeline-layer-label">{layer.name}</div>
                  <div className="timeline-layer-track">
                    <div
                      className={`timeline-layer-clip ${layer.type} ${selectedLayer?.id === layer.id ? "selected" : ""}`}
                      style={getClipStyle(layer)}
                      onMouseDown={(e) => handleTimelineMouseDown(e, layer, "move")}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLayer(layer);
                      }}
                    >
                      <div
                        className="timeline-layer-handle timeline-layer-handle-left"
                        onMouseDown={(e) => handleTimelineMouseDown(e, layer, "resize-left")}
                      />
                      {layer.name}
                      <div
                        className="timeline-layer-handle timeline-layer-handle-right"
                        onMouseDown={(e) => handleTimelineMouseDown(e, layer, "resize-right")}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Property Panel */}
        <div className="w-full md:w-72 bg-white border-t md:border-t-0 md:border-l border-slate-200 overflow-y-auto order-3 md:order-none max-h-96 md:max-h-none">
          <div className="p-4">
            <h3 className="font-semibold mb-4">Properties</h3>

            {selectedLayer ? (
              <div className="space-y-6">
                {/* Layer Info */}
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-medium text-slate-600 hover:text-slate-900">
                    Layer Settings
                    <ChevronDown className="w-4 h-4" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3 space-y-3">
                    <div>
                      <Label className="text-xs">Name</Label>
                      <Input
                        value={selectedLayer.name}
                        onChange={(e) => handleUpdateLayer(selectedLayer.id, { name: e.target.value })}
                        className="h-9"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">User Editable</Label>
                      <Switch
                        checked={selectedLayer.editable}
                        onCheckedChange={(v) => handleUpdateLayer(selectedLayer.id, { editable: v })}
                      />
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="w-full"
                      onClick={() => handleDeleteLayer(selectedLayer.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Layer
                    </Button>
                  </CollapsibleContent>
                </Collapsible>

                {/* Media Upload */}
                {(selectedLayer.type === "image" || selectedLayer.type === "video") && (
                  <>
                    <Collapsible>
                      <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-medium text-slate-600 hover:text-slate-900">
                        Media
                        <ChevronDown className="w-4 h-4" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-3">
                        <label className="block cursor-pointer">
                          <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-violet-300 transition-colors">
                            <Upload className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                            <span className="text-sm text-slate-600">
                              {selectedLayer.type === "video" ? "Upload image, video, or mp3" : `Upload ${selectedLayer.type}`}
                            </span>
                          </div>
                          <input
                            type="file"
                            accept={selectedLayer.type === "image" 
                              ? "image/*" 
                              : "video/*,image/*,audio/mpeg,.mp3,.webm,.mov,.mp4,.avi,.mkv,.jpg,.jpeg,.png,.gif,.webp"}
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                handleMediaUpload(selectedLayer.id, e.target.files[0]);
                              }
                            }}
                          />
                        </label>
                        {selectedLayer.media_url && (
                          <div className="mt-3 p-2 bg-slate-50 rounded text-xs text-slate-500 truncate">
                            {selectedLayer.media_url.split("/").pop()}
                          </div>
                        )}
                        {selectedLayer.type === "video" && selectedLayer.media_url && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full mt-3"
                            onClick={() => handleRemoveMedia(selectedLayer.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Remove image, video, or audio
                          </Button>
                        )}
                        {selectedLayer.type === "video" && (
                          <div className="mt-3 flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <div>
                              <Label className="text-xs font-medium">Add Recording capability</Label>
                              <p className="text-xs text-slate-500 mt-1">
                                Shows a camera recording button below the upload button in the frontend editor.
                              </p>
                            </div>
                            <Switch
                              checked={Boolean(selectedLayer.add_recording_capability)}
                              onCheckedChange={(v) => handleUpdateLayer(selectedLayer.id, { add_recording_capability: v })}
                            />
                          </div>
                        )}
                        {selectedLayer.type === "video" && (
                          <div className="mt-3 flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <div>
                              <Label className="text-xs font-medium">Music type</Label>
                              <p className="text-xs text-slate-500 mt-1">
                                Hides this media layer on the canvas while still letting uploaded MP3/audio play in the timeline and export.
                              </p>
                            </div>
                            <Switch
                              checked={Boolean(selectedLayer.music_type)}
                              onCheckedChange={(v) => handleUpdateLayer(selectedLayer.id, { music_type: v })}
                            />
                          </div>
                        )}
                        {selectedLayer.type === "video" && selectedLayer.music_type && (
                          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <div className="mb-2 flex items-center justify-between">
                              <Label className="text-xs font-medium">Volume</Label>
                              <span className="text-xs text-slate-600">{selectedLayer.media_volume ?? 100}%</span>
                            </div>
                            <Slider
                              value={[selectedLayer.media_volume ?? 100]}
                              onValueChange={(v) => handleUpdateLayer(selectedLayer.id, { media_volume: v[0] })}
                              min={0}
                              max={100}
                              step={1}
                              className="flex-1"
                            />
                            <p className="text-xs text-slate-500 mt-2">Adjusts the music volume in the editor preview and final export.</p>
                          </div>
                        )}
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Object Position - only show if media is uploaded */}
                    {selectedLayer.media_url && (
                      <Collapsible>
                        <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-medium text-slate-600 hover:text-slate-900">
                          Media Position
                          <ChevronDown className="w-4 h-4" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-3 space-y-4">
                          <div>
                            <Label className="text-xs mb-2 block">Media Fit</Label>
                            <Select
                              value={selectedLayer.media_fit || "cover"}
                              onValueChange={(value) => handleUpdateLayer(selectedLayer.id, { media_fit: value })}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="cover">Cover (crop to fill)</SelectItem>
                                <SelectItem value="contain">Contain (no crop)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label className="text-xs mb-2 block">Horizontal Position</Label>
                            <div className="flex items-center gap-3">
                              <Slider
                                value={[selectedLayer.objectPositionX || 50]}
                                onValueChange={(v) => handleUpdateLayer(selectedLayer.id, { objectPositionX: v[0] })}
                                min={0}
                                max={100}
                                step={1}
                                className="flex-1"
                              />
                              <span className="text-xs text-slate-600 w-12">{selectedLayer.objectPositionX || 50}%</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              0% = Left, 50% = Center, 100% = Right
                            </p>
                          </div>

                          <div>
                            <Label className="text-xs mb-2 block">Vertical Position</Label>
                            <div className="flex items-center gap-3">
                              <Slider
                                value={[selectedLayer.objectPositionY || 50]}
                                onValueChange={(v) => handleUpdateLayer(selectedLayer.id, { objectPositionY: v[0] })}
                                min={0}
                                max={100}
                                step={1}
                                className="flex-1"
                              />
                              <span className="text-xs text-slate-600 w-12">{selectedLayer.objectPositionY || 50}%</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              0% = Top, 50% = Center, 100% = Bottom
                            </p>
                          </div>

                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => handleUpdateLayer(selectedLayer.id, { objectPositionX: 50, objectPositionY: 50 })}
                          >
                            Reset to Center
                          </Button>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </>
                )}

                {/* AI Settings */}
                {selectedLayer.type === "image" && (
                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-medium text-slate-600 hover:text-slate-900">
                      <span className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-teal-500" />
                        PixVerse AI
                      </span>
                      <ChevronDown className="w-4 h-4" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3 space-y-3">
                      {/* Enable/Disable PixVerse AI */}
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <Label className="text-xs font-medium">Enable PixVerse AI</Label>
                          <p className="text-xs text-slate-500 mt-0.5">Allow users to animate images with AI</p>
                        </div>
                        <Switch
                          checked={selectedLayer.pixverse_enabled !== false}
                          onCheckedChange={(v) => handleUpdateLayer(selectedLayer.id, { pixverse_enabled: v })}
                        />
                      </div>

                      {/* Show settings only if enabled */}
                      {selectedLayer.pixverse_enabled !== false && (
                        <>
                          <div>
                            <Label className="text-xs">AI Prompt</Label>
                            <Textarea
                              value={selectedLayer.pixverse_prompt || ""}
                              onChange={(e) => handleUpdateLayer(selectedLayer.id, { pixverse_prompt: e.target.value })}
                              placeholder="Describe the animation..."
                              rows={3}
                              className="text-sm"
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              Users will upload an image, and this prompt will animate it
                            </p>
                          </div>
						  <div>
                            <Label className="text-xs">Duration</Label>
                            <Select
                              value={String(selectedLayer.pixverse_duration ?? 5)}
                              onValueChange={(v) =>
                                handleUpdateLayer(selectedLayer.id, { pixverse_duration: Number(v) })
                              }
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select duration" />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from({ length: 15 }, (_, i) => i + 1).map((d) => (
                                  <SelectItem key={d} value={String(d)}>
                                    {d}s
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500 mt-1">
                              This duration will be used when generating the PixVerse animation
                            </p>
                          </div>
                          <div className="flex items-center justify-between">
                            <Label className="text-xs">Freeze Last Frame</Label>
                            <Switch
                              checked={selectedLayer.freeze_last_frame}
                              onCheckedChange={(v) => handleUpdateLayer(selectedLayer.id, { freeze_last_frame: v })}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <Label className="text-xs">Freeze First Frame</Label>
                              <p className="text-xs text-slate-500 mt-0.5">
                                If the generated clip is shorter than the layer, hold the first frame before playback
                              </p>
                            </div>
                            <Switch
                              checked={selectedLayer.freeze_first_frame || false}
                              onCheckedChange={(v) => handleUpdateLayer(selectedLayer.id, { freeze_first_frame: v })}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <Label className="text-xs">Reverse clip</Label>
                              <p className="text-xs text-slate-500 mt-0.5">
                                Export PixVerse clips backwards so they end on the uploaded image
                              </p>
                            </div>
                            <Switch
                              checked={selectedLayer.pixverse_reverse_clip !== false}
                              onCheckedChange={(v) => handleUpdateLayer(selectedLayer.id, { pixverse_reverse_clip: v })}
                            />
                          </div>
                        </>
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {/* Text Settings */}
                {selectedLayer.type === "text" && (
                  <>
                    <Collapsible defaultOpen>
                      <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-medium text-slate-600 hover:text-slate-900">
                        Text Content
                        <ChevronDown className="w-4 h-4" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-3">
                        <Textarea
                          ref={textContentInputRef}
                          value={localTextContent}
                          onChange={(e) => handleTextContentChange(e.target.value)}
                          placeholder="Enter text..."
                          rows={3}
                        />
                        <div className="mt-2 flex flex-wrap gap-2">
                          {QUICK_INSERT_EMOJIS.map((emoji) => (
                            <button
                              key={emoji}
                              type="button"
                              className="h-8 w-8 rounded border border-slate-200 bg-white text-lg leading-none hover:bg-slate-50"
                              onClick={() => handleInsertEmoji(emoji)}
                              aria-label={`Insert ${emoji}`}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Text Styling */}
                    <Collapsible>
                      <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-medium text-slate-600 hover:text-slate-900">
                        Text Styling
                        <ChevronDown className="w-4 h-4" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-3 space-y-4">
                        {/* Font Family */}
                        <div>
                          <Label className="text-xs">Font</Label>
                          <Select
                            value={selectedLayer.fontFamily || "Arial"}
                            onValueChange={(v) => handleUpdateLayer(selectedLayer.id, { fontFamily: v })}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {GOOGLE_FONTS.map((font) => (
                                <SelectItem key={font} value={font} style={{ fontFamily: font }}>
                                  {font}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Text Style (Bold/Italic) */}
                        <div>
                          <Label className="text-xs mb-2 block">Text Style</Label>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant={selectedLayer.fontWeight === "bold" ? "default" : "outline"}
                              size="sm"
                              className="flex-1"
                              onClick={() => handleUpdateLayer(selectedLayer.id, { 
                                fontWeight: selectedLayer.fontWeight === "bold" ? "normal" : "bold" 
                              })}
                            >
                              <Bold className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant={selectedLayer.fontStyle === "italic" ? "default" : "outline"}
                              size="sm"
                              className="flex-1"
                              onClick={() => handleUpdateLayer(selectedLayer.id, { 
                                fontStyle: selectedLayer.fontStyle === "italic" ? "normal" : "italic" 
                              })}
                            >
                              <Italic className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Text Alignment */}
                        <div>
                          <Label className="text-xs mb-2 block">Alignment</Label>
                          <div className="grid grid-cols-4 gap-2">
                            <Button
                              type="button"
                              variant={selectedLayer.textAlign === "left" ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleUpdateLayer(selectedLayer.id, { textAlign: "left" })}
                            >
                              <AlignLeft className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant={selectedLayer.textAlign === "center" ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleUpdateLayer(selectedLayer.id, { textAlign: "center" })}
                            >
                              <AlignCenter className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant={selectedLayer.textAlign === "right" ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleUpdateLayer(selectedLayer.id, { textAlign: "right" })}
                            >
                              <AlignRight className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant={selectedLayer.textAlign === "justify" ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleUpdateLayer(selectedLayer.id, { textAlign: "justify" })}
                            >
                              <AlignJustify className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Text Color */}
                        <div>
                          <Label className="text-xs">Text Color</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={selectedLayer.textColor || "#000000"}
                              onChange={(e) => handleUpdateLayer(selectedLayer.id, { textColor: e.target.value })}
                              className="h-8 w-16"
                            />
                            <Input
                              type="text"
                              value={selectedLayer.textColor || "#000000"}
                              onChange={(e) => handleUpdateLayer(selectedLayer.id, { textColor: e.target.value })}
                              placeholder="#000000"
                              className="h-8 flex-1"
                            />
                          </div>
                        </div>

                        {/* Text Stroke */}
                        <div>
                          <Label className="text-xs">Text Stroke Color</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={selectedLayer.textStrokeColor || "#000000"}
                              onChange={(e) => handleUpdateLayer(selectedLayer.id, { textStrokeColor: e.target.value })}
                              className="h-8 w-16"
                            />
                            <Input
                              type="text"
                              value={selectedLayer.textStrokeColor || "#000000"}
                              onChange={(e) => handleUpdateLayer(selectedLayer.id, { textStrokeColor: e.target.value })}
                              placeholder="#000000"
                              className="h-8 flex-1"
                            />
                          </div>
                        </div>

                        <div>
                          <Label className="text-xs">Text Stroke Width</Label>
                          <div className="flex items-center gap-3">
                            <Slider
                              value={[selectedLayer.textStrokeWidth || 0]}
                              onValueChange={(v) => handleUpdateLayer(selectedLayer.id, { textStrokeWidth: v[0] })}
                              min={0}
                              max={20}
                              step={1}
                              className="flex-1"
                            />
                            <span className="text-xs text-slate-600 w-12">{(selectedLayer.textStrokeWidth || 0).toFixed(0)}px</span>
                          </div>
                        </div>

                        {/* Background Color */}
                        <div>
                          <Label className="text-xs">Background Color</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={selectedLayer.backgroundColor || "#ffffff"}
                              onChange={(e) => handleUpdateLayer(selectedLayer.id, { backgroundColor: e.target.value })}
                              className="h-8 w-16"
                            />
                            <Input
                              type="text"
                              value={selectedLayer.backgroundColor || "#ffffff"}
                              onChange={(e) => handleUpdateLayer(selectedLayer.id, { backgroundColor: e.target.value })}
                              placeholder="#ffffff"
                              className="h-8 flex-1"
                            />
                          </div>
                        </div>

                        {/* Background Opacity */}
                        <div>
                          <Label className="text-xs">Background Opacity</Label>
                          <div className="flex items-center gap-3">
                            <Slider
                              value={[selectedLayer.backgroundOpacity || 0]}
                              onValueChange={(v) => handleUpdateLayer(selectedLayer.id, { backgroundOpacity: v[0] })}
                              min={0}
                              max={1}
                              step={0.1}
                              className="flex-1"
                            />
                            <span className="text-xs text-slate-600 w-8">{((selectedLayer.backgroundOpacity || 0) * 100).toFixed(0)}%</span>
                          </div>
                        </div>

                        {/* Background Border Color */}
                        <div>
                          <Label className="text-xs">Border Color</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={selectedLayer.borderColor || "#000000"}
                              onChange={(e) => handleUpdateLayer(selectedLayer.id, { borderColor: e.target.value })}
                              className="h-8 w-16"
                            />
                            <Input
                              type="text"
                              value={selectedLayer.borderColor || "#000000"}
                              onChange={(e) => handleUpdateLayer(selectedLayer.id, { borderColor: e.target.value })}
                              placeholder="#000000"
                              className="h-8 flex-1"
                            />
                          </div>
                        </div>

                        {/* Background Border Width */}
                        <div>
                          <Label className="text-xs">Border Width</Label>
                          <div className="flex items-center gap-3">
                            <Slider
                              value={[selectedLayer.borderWidth || 0]}
                              onValueChange={(v) => handleUpdateLayer(selectedLayer.id, { borderWidth: v[0] })}
                              min={0}
                              max={20}
                              step={1}
                              className="flex-1"
                            />
                            <span className="text-xs text-slate-600 w-12">{(selectedLayer.borderWidth || 0).toFixed(0)}px</span>
                          </div>
                        </div>

                        {/* Line Spacing */}
                        <div>
                          <Label className="text-xs">Line Spacing</Label>
                          <div className="flex items-center gap-3">
                            <Slider
                              value={[selectedLayer.lineHeight || 1.5]}
                              onValueChange={(v) => handleUpdateLayer(selectedLayer.id, { lineHeight: v[0] })}
                              min={0.8}
                              max={3}
                              step={0.1}
                              className="flex-1"
                            />
                            <span className="text-xs text-slate-600 w-12">{(selectedLayer.lineHeight || 1.5).toFixed(1)}</span>
                          </div>
                        </div>

                        {/* Border Radius */}
                        <div>
                          <Label className="text-xs">Corner Radius (px)</Label>
                          <Input
                            type="number"
                            value={selectedLayer.borderRadius || 0}
                            onChange={(e) => handleUpdateLayer(selectedLayer.id, { borderRadius: Number(e.target.value) })}
                            min={0}
                            max={100}
                            step={1}
                            className="h-8"
                            placeholder="0"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            0 = Sharp corners, Higher = Rounder corners
                          </p>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </>
                )}

                {selectedLayer.type === "shape" && (
                  <Collapsible defaultOpen>
                    <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-medium text-slate-600 hover:text-slate-900">
                      Shape Settings <ChevronDown className="w-4 h-4" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3">
                      <ShapeSettings layer={selectedLayer} onChange={(key, value) => handleUpdateLayer(selectedLayer.id, { [key]: value })} />
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {/* Transform */}
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-medium text-slate-600 hover:text-slate-900">
                    Transform
                    <ChevronDown className="w-4 h-4" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3 space-y-4">
                    {selectedLayer.type === "text" && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs">Position</Label>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs" htmlFor="position-presets-enabled">Enable</Label>
                            <Switch
                              id="position-presets-enabled"
                              checked={selectedLayer.position_presets_enabled !== false}
                              onCheckedChange={(value) => handleUpdateLayer(selectedLayer.id, {
                                position_presets_enabled: value,
                              })}
                            />
                          </div>
                        </div>
                        <Select
                          value={getTextPositionPreset(selectedLayer.position_y)}
                          disabled={selectedLayer.position_presets_enabled === false}
                          onValueChange={(value) => handleUpdateLayer(selectedLayer.id, {
                            position_y: TEXT_POSITION_PRESETS[value],
                          })}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="top">Top</SelectItem>
                            <SelectItem value="middle">Middle</SelectItem>
                            <SelectItem value="bottom">Bottom</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {selectedLayer.type === "text" && (
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-xs" htmlFor="stay-centered">Stay centered</Label>
                          <p className="text-xs text-slate-500 mt-1">
                            Keep the same horizontal center when users change the width.
                          </p>
                        </div>
                        <Switch
                          id="stay-centered"
                          checked={selectedLayer.stay_centered !== false}
                          onCheckedChange={(value) => handleUpdateLayer(selectedLayer.id, {
                            stay_centered: value,
                          })}
                        />
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">X Position</Label>
                        <Input
                          type="number"
                          value={selectedLayer.position_x}
                          onChange={(e) => handleUpdateLayer(selectedLayer.id, { position_x: Number(e.target.value) })}
                          className="h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Y Position</Label>
                        <Input
                          type="number"
                          value={selectedLayer.position_y}
                          onChange={(e) => handleUpdateLayer(selectedLayer.id, { position_y: Number(e.target.value) })}
                          className="h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Width %</Label>
                        <Input
                          type="number"
                          value={selectedLayer.width}
                          onChange={(e) => handleUpdateLayer(selectedLayer.id, { width: Number(e.target.value) })}
                          className="h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Height %</Label>
                        <Input
                          type="number"
                          value={selectedLayer.height}
                          onChange={(e) => handleUpdateLayer(selectedLayer.id, { height: Number(e.target.value) })}
                          className="h-9"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Opacity</Label>
                      <div className="flex items-center gap-3">
                        <Slider
                          value={[selectedLayer.opacity]}
                          max={1}
                          step={0.01}
                          onValueChange={([v]) => handleUpdateLayer(selectedLayer.id, { opacity: v })}
                          className="flex-1"
                        />
                        <span className="text-xs text-slate-600 w-12">{((selectedLayer.opacity || 1) * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Scale</Label>
                      <div className="flex items-center gap-3">
                        <Slider
                          value={[selectedLayer.scale]}
                          min={0.1}
                          max={3}
                          step={0.01}
                          onValueChange={([v]) => handleUpdateLayer(selectedLayer.id, { scale: v })}
                          className="flex-1"
                        />
                        <span className="text-xs text-slate-600 w-12">{((selectedLayer.scale || 1) * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                    {(selectedLayer.type === "image" || selectedLayer.type === "video") && (
                      <div className="space-y-3 rounded-lg border border-slate-200 p-3 bg-slate-50">
                        <Label className="text-xs font-medium text-slate-700">Media Corner Radius (px)</Label>
                        <div>
                          <Label className="text-xs mb-1 block text-slate-600">All corners</Label>
                          <Input
                            type="number"
                            value={selectedLayer.borderRadius || 0}
                            onChange={(e) => handleUpdateLayer(selectedLayer.id, { borderRadius: Number(e.target.value) })}
                            min={0}
                            max={500}
                            step={1}
                            className="h-8"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {[
                            ["Top left", "borderTopLeftRadius"],
                            ["Top right", "borderTopRightRadius"],
                            ["Bottom left", "borderBottomLeftRadius"],
                            ["Bottom right", "borderBottomRightRadius"],
                          ].map(([label, key]) => (
                            <div key={key}>
                              <Label className="text-xs mb-1 block text-slate-600">{label}</Label>
                              <Input
                                type="number"
                                value={selectedLayer[key] ?? selectedLayer.borderRadius ?? 0}
                                onChange={(e) => handleUpdateLayer(selectedLayer.id, { [key]: Number(e.target.value) })}
                                min={0}
                                max={500}
                                step={1}
                                className="h-8"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>

                {/* Timing */}
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-medium text-slate-600 hover:text-slate-900">
                    Timing
                    <ChevronDown className="w-4 h-4" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3 space-y-3">
                    <div>
                      <Label className="text-xs">Start Time (s)</Label>
                      <Input
                        type="number"
                        min={0}
                        max={templateSettings.duration}
                        step={0.1}
                        value={selectedLayer.start_time}
                        onChange={(e) => handleUpdateLayer(selectedLayer.id, { start_time: Number(e.target.value) })}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Duration (s)</Label>
                      <Input
                        type="number"
                        min={0.1}
                        step={0.1}
                        value={selectedLayer.duration}
                        onChange={(e) => handleUpdateLayer(selectedLayer.id, { duration: Number(e.target.value) })}
                        className="h-9"
                      />
                      {selectedLayer.type === "video" && selectedLayer.media_url && selectedLayer.media_type === "video" && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-2 w-full"
                          onClick={handleMatchUploadedVideoDuration}
                        >
                          Match uploaded video duration
                        </Button>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Animations */}
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-medium text-slate-600 hover:text-slate-900">
                    <span className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-purple-500" />
                      Animations
                    </span>
                    <ChevronDown className="w-4 h-4" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3 space-y-4">
                    {/* Entrance Animation */}
                    <div>
                      <Label className="text-xs mb-2 block">Entrance Animation</Label>
                      <Select
                        value={selectedLayer.entrance_animation || "none"}
                        onValueChange={(v) => handleUpdateLayer(selectedLayer.id, { entrance_animation: v })}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="fade">Fade In</SelectItem>
                          <SelectItem value="slide_fade_from_left">Slide &amp; Fade from Left</SelectItem>
                          <SelectItem value="slide_fade_from_right">Slide &amp; Fade from Right</SelectItem>
                          <SelectItem value="slide_fade_from_top">Slide &amp; Fade from Top</SelectItem>
                          <SelectItem value="slide_fade_from_bottom">Slide &amp; Fade from Bottom</SelectItem>
                          <SelectItem value="circular_wipe">Circular Wipe In</SelectItem>
                          <SelectItem value="flash">Camera Flash In</SelectItem>
                          <SelectItem value="slide_left">Slide from Right</SelectItem>
                          <SelectItem value="slide_right">Slide from Left</SelectItem>
                          <SelectItem value="slide_up">Slide from Bottom</SelectItem>
                          <SelectItem value="slide_down">Slide from Top</SelectItem>
                          {selectedLayer.type === "text" && <SelectItem value="text_wipe">Text wipe</SelectItem>}
                          <SelectItem value="wipe_left">Wipe from Left</SelectItem>
                          <SelectItem value="wipe_right">Wipe from Right</SelectItem>
                          <SelectItem value="wipe_up">Wipe from Top</SelectItem>
                          <SelectItem value="wipe_down">Wipe from Bottom</SelectItem>
                          <SelectItem value="zoom_in">Zoom In</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {(selectedLayer.entrance_animation || "none") !== "none" && (
                      <>
                        <div>
                          <Label className="text-xs mb-2 block">Entrance Easing</Label>
                          <Select
                            value={selectedLayer.entrance_easing || "linear"}
                            onValueChange={(v) => handleUpdateLayer(selectedLayer.id, { entrance_easing: v })}
                          >
                            <SelectTrigger className="h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="linear">Linear</SelectItem>
                              <SelectItem value="ease_in">Ease In</SelectItem>
                              <SelectItem value="ease_out">Ease Out</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs mb-2 block">Entrance Animation Duration (s)</Label>
                          <div className="flex items-center gap-3">
                            <Slider
                              value={[selectedLayer.entrance_animation_duration ?? selectedLayer.animation_duration ?? 0.5]}
                              onValueChange={(v) => handleUpdateLayer(selectedLayer.id, { entrance_animation_duration: v[0] })}
                              min={0.1}
                              max={3}
                              step={0.1}
                              className="flex-1"
                            />
                            <span className="text-xs text-slate-600 w-12">{(selectedLayer.entrance_animation_duration ?? selectedLayer.animation_duration ?? 0.5).toFixed(1)}s</span>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Exit Animation */}
                    <div>
                      <Label className="text-xs mb-2 block">Exit Animation</Label>
                      <Select
                        value={selectedLayer.exit_animation || "none"}
                        onValueChange={(v) => handleUpdateLayer(selectedLayer.id, { exit_animation: v })}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="fade">Fade Out</SelectItem>
                          <SelectItem value="slide_fade_to_left">Slide &amp; Fade to Left</SelectItem>
                          <SelectItem value="slide_fade_to_right">Slide &amp; Fade to Right</SelectItem>
                          <SelectItem value="slide_fade_to_top">Slide &amp; Fade to Top</SelectItem>
                          <SelectItem value="slide_fade_to_bottom">Slide &amp; Fade to Bottom</SelectItem>
                          <SelectItem value="circular_wipe">Circular Wipe Out</SelectItem>
                          <SelectItem value="flash">Camera Flash Out</SelectItem>
                          <SelectItem value="slide_left">Slide to Left</SelectItem>
                          <SelectItem value="slide_right">Slide to Right</SelectItem>
                          <SelectItem value="slide_up">Slide to Top</SelectItem>
                          <SelectItem value="slide_down">Slide to Bottom</SelectItem>
                          {selectedLayer.type === "text" && <SelectItem value="text_wipe">Text wipe</SelectItem>}
                          <SelectItem value="wipe_left">Wipe to Left</SelectItem>
                          <SelectItem value="wipe_right">Wipe to Right</SelectItem>
                          <SelectItem value="wipe_up">Wipe to Top</SelectItem>
                          <SelectItem value="wipe_down">Wipe to Bottom</SelectItem>
                          <SelectItem value="zoom_out">Zoom Out</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {(selectedLayer.exit_animation || "none") !== "none" && (
                      <>
                        <div>
                          <Label className="text-xs mb-2 block">Exit Easing</Label>
                          <Select
                            value={selectedLayer.exit_easing || "linear"}
                            onValueChange={(v) => handleUpdateLayer(selectedLayer.id, { exit_easing: v })}
                          >
                            <SelectTrigger className="h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="linear">Linear</SelectItem>
                              <SelectItem value="ease_in">Ease In</SelectItem>
                              <SelectItem value="ease_out">Ease Out</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs mb-2 block">Exit Animation Duration (s)</Label>
                          <div className="flex items-center gap-3">
                            <Slider
                              value={[selectedLayer.exit_animation_duration ?? selectedLayer.animation_duration ?? 0.5]}
                              onValueChange={(v) => handleUpdateLayer(selectedLayer.id, { exit_animation_duration: v[0] })}
                              min={0.1}
                              max={3}
                              step={0.1}
                              className="flex-1"
                            />
                            <span className="text-xs text-slate-600 w-12">{(selectedLayer.exit_animation_duration ?? selectedLayer.animation_duration ?? 0.5).toFixed(1)}s</span>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Continuous Animation */}
                    <div>
                      <Label className="text-xs mb-2 block">Continuous Animation</Label>
                      <Select
                        value={selectedLayer.continuous_animation || "none"}
                        onValueChange={(v) => handleUpdateLayer(selectedLayer.id, { continuous_animation: v })}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="bounce">Bounce</SelectItem>
                          <SelectItem value="pulse">Pulse</SelectItem>
                          <SelectItem value="rotate">Rotate</SelectItem>
                          <SelectItem value="ken_burns">Ken Burns</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Loop Duration for Continuous Animation */}
                    {selectedLayer.continuous_animation !== "none" && selectedLayer.continuous_animation && (
                      <>
                        {selectedLayer.continuous_animation !== "ken_burns" && (
                          <div>
                            <Label className="text-xs mb-2 block">Loop Duration (s)</Label>
                            <div className="flex items-center gap-3">
                              <Slider
                                value={[selectedLayer.loop_duration || 1.0]}
                                onValueChange={(v) => handleUpdateLayer(selectedLayer.id, { loop_duration: v[0] })}
                                min={0.5}
                                max={5}
                                step={0.1}
                                className="flex-1"
                              />
                              <span className="text-xs text-slate-600 w-12">{(selectedLayer.loop_duration || 1.0).toFixed(1)}s</span>
                            </div>
                          </div>
                        )}
                        {(selectedLayer.continuous_animation === "ken_burns") && (
                          <div>
                            <Label className="text-xs mb-2 block">Ken Burns End Scale</Label>
                            <Input
                              type="number"
                              min={1}
                              max={2}
                              step={0.01}
                              value={selectedLayer.ken_burns_end_scale ?? 1.05}
                              onChange={(e) => handleUpdateLayer(selectedLayer.id, { ken_burns_end_scale: Number(e.target.value) })}
                              className="h-9"
                            />
                          </div>
                        )}
                        <div>
                          <Label className="text-xs mb-2 block">Continuous Start / End (%)</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              value={selectedLayer.continuous_start_percent ?? 0}
                              onChange={(e) => handleUpdateLayer(selectedLayer.id, { continuous_start_percent: Number(e.target.value) })}
                              className="h-9"
                            />
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              value={selectedLayer.continuous_end_percent ?? 100}
                              onChange={(e) => handleUpdateLayer(selectedLayer.id, { continuous_end_percent: Number(e.target.value) })}
                              className="h-9"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <p className="text-xs text-purple-700">
                        💡 Animations will be applied when rendering the final video
                      </p>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            ) : (
              <div className="text-center py-12 px-4">
                <Layers className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-sm font-medium text-slate-700 mb-1">No Layer Selected</p>
                <p className="text-xs text-slate-500">Click on a layer in the canvas or layer list to view and edit its properties</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
